/**
 * AgentenKommandozentrale — Admin-Only Slide-Panel von rechts
 * 
 * V4.3 — CI-Farben, Zebra-Linien, Anti-Flicker, Status-Dashboard
 *
 * 7 Tabs: Vorschläge | Protokoll | Agenten | Lernregeln | Roadmap | Status | Einstellungen
 * 
 * Design: Koop CI (#C41E3A / #9B1C1C), Schatten, Zebra-Linien, Montserrat/Inter
 * Anti-Flicker: Stabile useRef, einmaliges Laden, optimistische Updates
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Sheet,
  SheetContent,
  SheetClose,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  useAgentenSystem,
  type AgentenVorschlag,
  type LernRegel,
  type AgentBriefing,
  type ProtokollEintrag,
  type Entscheidung,
  type TabId,
  type KontextEintrag,
  type AufgabenDetails,
  type IssueDetails,
  type Mitarbeiter,
  BEREICHE,
  loadKontextForVorschlag,
} from '@/hooks/useAgentenSystem';
import {
  RefreshCw, Check, X, Edit3, Send, ChevronDown, ChevronUp,
  Loader2, Settings, Zap, Clock, AlertCircle,
  CheckCircle2, XCircle, MessageSquare, TrendingUp, Mail,
  FileText, Wallet, FolderOpen, Brain, Shield, Plus,
  ToggleLeft, ToggleRight, Sparkles, Activity, History,
  ClipboardList, FileSearch, Landmark, Building, Globe,
  FolderSearch, HeartPulse, ShieldCheck, Car, Diamond,
  Package, User, Eye, ListChecks, Archive, Trash2,
  MailX, FolderInput, Receipt, PenTool, Radio, Server,
  Home, CircleDot, Map, ArrowUpDown, ThumbsUp, ThumbsDown,
  Lightbulb, Rocket, Bug, Wrench, Target, CalendarClock,
  Timer, CalendarDays, Layers, Filter,
  Download, Upload, RefreshCcw, Link2, Plug, AlertTriangle, KeyRound,
  Calculator, Bot, Lock, Github, ImageIcon, Camera, ExternalLink,
  MessageCircle, GitBranch, Hash, Paperclip, Search,
} from 'lucide-react';
import { toast } from 'sonner';
import { useKiCockpit, type KiCockpitEintrag } from '@/hooks/useKiCockpit';
import { ROADMAP_SYNC_ITEMS, ROADMAP_STATS, type RoadmapSyncItem } from '@/data/roadmapSync';
import { useGitHubRoadmap, GITHUB_PROJEKTE, type GitHubIssue, type IssueFeedback } from '@/hooks/useGitHubRoadmap';
import RoadmapTabV2 from '@/components/RoadmapTabV2';
import SystemStatusTab from '@/components/SystemStatusTab';
import AgentenChatTab from '@/components/AgentenChatTab';
import { supabase } from '@/lib/supabase';

// ═══════════════════════════════════════════════════════════════════════════
// CI DESIGN TOKENS — Koop Corporate Identity
// ═══════════════════════════════════════════════════════════════════════════

const CI = {
  // Primärfarben
  red: '#C41E3A',
  redDark: '#9B1C1C',
  redLight: '#FEF2F2',
  redBorder: '#FECACA',
  // Hintergrund
  bgPanel: '#FFFFFF',
  bgSection: '#F9FAFB',
  bgZebraEven: '#FFFFFF',
  bgZebraOdd: '#F8F9FB',
  bgHover: '#F3F4F6',
  // Text
  textPrimary: '#111827',
  textSecondary: '#4B5563',
  textMuted: '#9CA3AF',
  textLight: '#D1D5DB',
  // Borders & Shadows
  border: '#E5E7EB',
  borderLight: '#F3F4F6',
  shadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)',
  shadowHover: '0 4px 12px rgba(0,0,0,0.10), 0 2px 4px rgba(0,0,0,0.06)',
  // Akzente
  emerald: '#059669',
  emeraldBg: '#ECFDF5',
  amber: '#D97706',
  amberBg: '#FFFBEB',
  blue: '#2563EB',
  blueBg: '#EFF6FF',
  purple: '#7C3AED',
  purpleBg: '#F5F3FF',
  cyan: '#0891B2',
  cyanBg: '#ECFEFF',
} as const;

// ─── Icon-Map: String → Lucide-Komponente ──────────────────────────────────

const ICON_MAP: Record<string, React.ElementType> = {
  ClipboardList, FileSearch, Landmark, Building, Globe, Mail,
  FolderSearch, HeartPulse, ShieldCheck, Car, Diamond, Package,
  User, Activity, History, Brain, Settings, Sparkles,
  FileText, Wallet, FolderOpen, MessageSquare, AlertCircle,
  CheckCircle2, XCircle, TrendingUp, Zap, Clock, Eye,
  ListChecks, Archive, Trash2, MailX, FolderInput, Receipt,
  PenTool, Radio, Server, Home, CircleDot,
  Calculator, Bot, Lock, Shield, AlertTriangle, Plug, Wrench,
  Lightbulb, Rocket, Bug, Target, CalendarClock,
  Github, ImageIcon, Camera, ExternalLink,
  MessageCircle, GitBranch, Hash, Paperclip,
};

function getIcon(name: string): React.ElementType {
  return ICON_MAP[name] || CircleDot;
}

// ─── Zeitformatierung ──────────────────────────────────────────────────────

function zeitAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMin = Math.floor((now - then) / 60000);
  if (diffMin < 1) return 'Gerade eben';
  if (diffMin < 60) return `vor ${diffMin} Min`;
  if (diffMin < 1440) return `vor ${Math.floor(diffMin / 60)} Std`;
  if (diffMin < 10080) return `vor ${Math.floor(diffMin / 1440)} Tagen`;
  return new Date(dateStr).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

function formatRelativeTime(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'Gerade eben';
  if (diffMin < 60) return `Vor ${diffMin} Min.`;
  if (diffHrs < 24) return `Vor ${diffHrs} Std.`;
  if (diffDays < 7) return `Vor ${diffDays} Tagen`;
  return date.toLocaleDateString('de-DE');
}

// ─── Vorschlag-Typ-Konfiguration ───────────────────────────────────────────

const VORSCHLAG_TYP_CONFIG: Record<string, { icon: React.ElementType; label: string; color: string; bg: string }> = {
  email:          { icon: Mail,         label: 'E-Mail',          color: CI.red,    bg: CI.redLight },
  stellungnahme:  { icon: FileText,     label: 'Stellungnahme',   color: CI.blue,   bg: CI.blueBg },
  aufgabe:        { icon: CheckCircle2, label: 'Aufgabe',         color: CI.emerald,bg: CI.emeraldBg },
  ablage:         { icon: FolderOpen,   label: 'Ablage',          color: CI.purple, bg: CI.purpleBg },
  ueberweisung:   { icon: Wallet,      label: 'Überweisung',     color: CI.amber,  bg: CI.amberBg },
  antwort:        { icon: MessageSquare,label: 'Antwort',         color: CI.cyan,   bg: CI.cyanBg },
  allgemein:      { icon: Zap,          label: 'Allgemein',       color: CI.textSecondary, bg: CI.bgSection },
  meldung:        { icon: MessageSquare,label: 'Meldung',         color: CI.blue,   bg: CI.blueBg },
  manuell:        { icon: Lightbulb,    label: 'Manuell',         color: CI.amber,  bg: CI.amberBg },
  anweisung_global:{ icon: Radio,        label: 'Globale Anweisung',color: CI.red,   bg: CI.redLight },
  akte_zuordnung: { icon: FolderSearch,  label: 'Akte-Zuordnung',  color: CI.cyan,   bg: CI.cyanBg },
  rueckfrage:     { icon: AlertCircle,  label: 'Rückfrage',       color: CI.amber,  bg: CI.amberBg },
  fortschritt:    { icon: TrendingUp,   label: 'Fortschritt',     color: CI.purple, bg: CI.purpleBg },
  vorschlag:      { icon: Sparkles,     label: 'Vorschlag',       color: CI.emerald,bg: CI.emeraldBg },
  frist:          { icon: Timer,        label: 'Frist',           color: CI.red,    bg: CI.redLight },
  kalender:       { icon: CalendarDays, label: 'Termin',          color: CI.purple, bg: CI.purpleBg },
  projekt:        { icon: Layers,       label: 'Projekt',         color: '#1E3A5F', bg: '#EFF6FF' },
  warnung:        { icon: AlertTriangle,label: 'Warnung',         color: '#DC2626', bg: '#FEF2F2' },
  optimierung:    { icon: TrendingUp,   label: 'Optimierung',     color: CI.emerald,bg: CI.emeraldBg },
  integration:    { icon: Plug,         label: 'Integration',     color: CI.purple, bg: CI.purpleBg },
  feedback:       { icon: MessageCircle, label: 'Feedback',        color: CI.amber,  bg: CI.amberBg },
  github_issue:   { icon: Github,       label: 'GitHub Issue',    color: '#24292F', bg: CI.bgSection },
  issue:          { icon: Github,       label: 'GitHub Issue',    color: '#24292F', bg: CI.bgSection },
  dev_review:     { icon: Bot,          label: 'Dev-Review',      color: CI.purple, bg: CI.purpleBg },
};

// ─── Protokoll-Aktion-Konfiguration ────────────────────────────────────────

const AKTION_CONFIG: Record<string, { icon: React.ElementType; label: string; color: string; bg: string }> = {
  newsletter_abbestellt: { icon: MailX,       label: 'Newsletter abbestellt', color: CI.red,    bg: CI.redLight },
  email_kategorisiert:   { icon: Archive,     label: 'E-Mails sortiert',      color: CI.blue,   bg: CI.blueBg },
  spam_geloescht:        { icon: Trash2,      label: 'Spam gelöscht',         color: CI.textMuted, bg: CI.bgSection },
  dokument_abgelegt:     { icon: FolderInput, label: 'Dokument abgelegt',     color: CI.cyan,   bg: CI.cyanBg },
  rechnung_erfasst:      { icon: Receipt,     label: 'Rechnung erfasst',      color: CI.emerald,bg: CI.emeraldBg },
  stellungnahme_entwurf: { icon: PenTool,     label: 'Stellungnahme',         color: CI.purple, bg: CI.purpleBg },
  briefing_versendet:    { icon: Mail,        label: 'Briefing versendet',    color: '#1E3A5F', bg: '#EFF6FF' },
  deployment_geprueft:   { icon: Server,      label: 'Deployment geprüft',    color: CI.purple, bg: CI.purpleBg },
  miete_geprueft:        { icon: Home,        label: 'Miete geprüft',         color: CI.amber,  bg: CI.amberBg },
  system_gestartet:      { icon: Rocket,      label: 'System gestartet',      color: CI.emerald,bg: CI.emeraldBg },
  system_bereit:         { icon: CheckCircle2,label: 'Agent bereit',           color: CI.emerald,bg: CI.emeraldBg },
  dokument_analysiert:   { icon: Eye,         label: 'Dokument analysiert',   color: CI.cyan,   bg: CI.cyanBg },
  email_entworfen:       { icon: Edit3,       label: 'E-Mail entworfen',      color: CI.red,    bg: CI.redLight },
  aufgabe_erstellt:      { icon: Plus,        label: 'Aufgabe erstellt',      color: CI.blue,   bg: CI.blueBg },
  frist_erkannt:         { icon: Timer,       label: 'Frist erkannt',         color: CI.amber,  bg: CI.amberBg },
  zahlung_vorgeschlagen: { icon: Wallet,      label: 'Zahlung vorgeschlagen', color: CI.amber,  bg: CI.amberBg },
  regel_angewendet:      { icon: Brain,       label: 'Regel angewendet',      color: CI.purple, bg: CI.purpleBg },
  system_sync:            { icon: RefreshCcw,   label: 'System-Sync',           color: CI.blue,   bg: CI.blueBg },
  tuev_warnung:           { icon: AlertTriangle,label: 'TÜV-Warnung',           color: '#DC2626', bg: '#FEF2F2' },
  steuer_check:           { icon: Calculator,   label: 'Steuer-Check',          color: CI.amber,  bg: CI.amberBg },
  recht_check:            { icon: Shield,       label: 'Rechts-Check',          color: CI.purple, bg: CI.purpleBg },
  immobilien_check:       { icon: Home,         label: 'Immobilien-Check',      color: CI.cyan,   bg: CI.cyanBg },
  hr_check:               { icon: User,         label: 'HR-Check',              color: CI.emerald,bg: CI.emeraldBg },
  qualitaetskontrolle:    { icon: ShieldCheck,  label: 'Qualitätskontrolle',    color: CI.blue,   bg: CI.blueBg },
  vorschlag_erstellt:     { icon: Lightbulb,    label: 'Vorschlag erstellt',    color: CI.amber,  bg: CI.amberBg },
  manueller_vorschlag:    { icon: Lightbulb,    label: 'Manueller Vorschlag',   color: CI.amber,  bg: CI.amberBg },
  stellungnahme_vorbereitet: { icon: PenTool,   label: 'Stellungnahme vorbereitet', color: CI.purple, bg: CI.purpleBg },
  akte_zugeordnet:        { icon: FolderSearch, label: 'Akte zugeordnet',       color: CI.cyan,   bg: CI.cyanBg },
  anwalt_email_erkannt:   { icon: Mail,         label: 'Anwalts-E-Mail erkannt',color: CI.red,    bg: CI.redLight },
  winvalue_aktenzeichen:  { icon: FileSearch,   label: 'Winvalue-AZ erkannt',   color: '#1E3A5F', bg: '#EFF6FF' },
  email_agent_verarbeitet:{ icon: Bot,          label: 'E-Mail verarbeitet',    color: CI.blue,   bg: CI.blueBg },
  vorschlag_stellungnahme:{ icon: PenTool,      label: 'Stellungnahme',         color: CI.purple, bg: CI.purpleBg },
  vorschlag_akte_zuordnen:{ icon: FolderSearch, label: 'In Akte gelegt',        color: CI.cyan,   bg: CI.cyanBg },
  feedback_eingegangen:   { icon: MessageCircle,label: 'Feedback eingegangen',  color: CI.amber,  bg: CI.amberBg },
  github_issue_erstellt:  { icon: Github,       label: 'GitHub Issue erstellt', color: '#24292F', bg: CI.bgSection },
  dev_review_geprueft:    { icon: Bot,          label: 'Dev-Review',            color: CI.purple, bg: CI.purpleBg },
  dev_tipp_erstellt:      { icon: Lightbulb,    label: 'Dev-Tipp',              color: CI.amber,  bg: CI.amberBg },
};

// ═══════════════════════════════════════════════════════════════════════════
// VORSCHLAG-KARTE (Erweitert V5: Aktions-Leiste, E-Mail-Kontext, Routing)
// ═══════════════════════════════════════════════════════════════════════════

// Aktions-Typen die ein Vorschlag haben kann
const VORSCHLAG_AKTIONEN: { id: string; icon: React.ElementType; label: string; color: string; bg: string }[] = [
  { id: 'antworten',       icon: Send,         label: 'Antworten',          color: CI.blue,   bg: CI.blueBg },
  { id: 'aufgabe',         icon: CheckCircle2, label: 'Aufgabe',            color: CI.emerald,bg: CI.emeraldBg },
  { id: 'stellungnahme',   icon: PenTool,      label: 'Stellungnahme',      color: '#7C3AED', bg: '#F5F3FF' },
  { id: 'akte_zuordnen',   icon: FolderSearch,  label: 'In Akte legen',     color: '#0891B2', bg: '#ECFEFF' },
  { id: 'ablegen',         icon: FolderInput,  label: 'Ablegen',            color: CI.purple, bg: CI.purpleBg },
  { id: 'weiterleiten',    icon: ArrowUpDown,  label: 'Weiterleiten',       color: CI.cyan,   bg: CI.cyanBg },
  { id: 'archivieren',     icon: Archive,      label: 'Archivieren',        color: CI.textMuted, bg: CI.bgSection },
];

// Agenten-Liste für Routing-Dropdown
const ROUTING_AGENTEN = [
  { id: 'tagesmanager',    label: 'Tagesmanager',       icon: ClipboardList },
  { id: 'sv-buero',        label: 'SV Büro Agent',      icon: FileSearch },
  { id: 'buchhaltung',     label: 'Buchhaltung',        icon: Calculator },
  { id: 'email-agent',     label: 'E-Mail Agent',       icon: Mail },
  { id: 'dokument-agent',  label: 'Dokument Agent',     icon: FolderOpen },
  { id: 'finanz-agent',    label: 'Finanz-Agent',       icon: Landmark },
  { id: 'crm-agent',       label: 'CRM Agent',          icon: User },
  { id: 'werkstatt-agent', label: 'Werkstatt Agent',    icon: Wrench },
  { id: 'marketing-agent', label: 'Marketing Agent',    icon: Globe },
  { id: 'manus',           label: 'Manus (Entwicklung)',icon: Bot },
  { id: 'controller',      label: 'Controller',         icon: ShieldCheck },
  { id: 'steuern',         label: 'Steuer-Agent',       icon: Calculator },
  { id: 'recht',           label: 'Rechts-Agent',       icon: Shield },
  { id: 'hr',              label: 'HR-Agent',           icon: User },
  { id: 'immobilien',      label: 'Immobilien-Agent',   icon: Home },
  { id: 'fahrzeugmanager', label: 'Fahrzeugmanager',    icon: Car },
  { id: 'stellungnahme-assistent', label: 'Stellungnahmen-Assistent', icon: PenTool },
  { id: 'manus-dev', label: 'Manus Dev-Agent', icon: Github },
];

function VorschlagKarte({
  vorschlag,
  onEntscheiden,
  onAktion,
  onAufgabeErstellen,
  onIssueInRoadmap,
  mitarbeiter,
  saving,
  index,
  agenten,
}: {
  vorschlag: AgentenVorschlag;
  onEntscheiden: (id: string, e: Entscheidung, text?: string) => Promise<boolean>;
  onAktion: (id: string, aktion: string, details: Record<string, any>) => Promise<boolean>;
  onAufgabeErstellen: (id: string, details: AufgabenDetails) => Promise<boolean>;
  onIssueInRoadmap: (id: string, details: IssueDetails) => Promise<string | null>;
  mitarbeiter: Mitarbeiter[];
  saving: string | null;
  index: number;
  agenten: AgentBriefing[];
}) {
  const [showVerbesserung, setShowVerbesserung] = useState(false);
  const [showAktionen, setShowAktionen] = useState(false);
  const [showAnweisung, setShowAnweisung] = useState(false);
  const [showKontext, setShowKontext] = useState(false);
  const [kontextEintraege, setKontextEintraege] = useState<KontextEintrag[]>([]);
  const [kontextLoading, setKontextLoading] = useState(false);
  const [kontextLoaded, setKontextLoaded] = useState(false);
  const [verbesserungText, setVerbesserungText] = useState('');
  const [anweisungText, setAnweisungText] = useState('');
  const [gewaehlteAktion, setGewaehlteAktion] = useState<string | null>(null);
  const [antwortZeit, setAntwortZeit] = useState('');
  const [zielAgent, setZielAgent] = useState('');
  const isSaving = saving === vorschlag.id;

  // Task suggestion panel state
  const [showAufgabeEdit, setShowAufgabeEdit] = useState(false);
  const [aufgabeForm, setAufgabeForm] = useState<AufgabenDetails | null>(null);
  const [isSavingAufgabe, setIsSavingAufgabe] = useState(false);

  const assigneeName = useMemo(() => {
    const ad = vorschlag.aufgaben_details;
    if (!ad?.zugewiesen_an) return null;
    const m = mitarbeiter.find(x => x.id === ad.zugewiesen_an);
    if (m) return `${m.vorname} ${m.nachname}`;
    if (ad.zugewiesen_an === '11111111-1111-1111-1111-111111111111') return 'Andreas Koop';
    return ad.zugewiesen_an;
  }, [vorschlag.aufgaben_details, mitarbeiter]);

  const handleAufgabeBearbeiten = useCallback(() => {
    if (vorschlag.aufgaben_details) {
      setAufgabeForm({ ...vorschlag.aufgaben_details });
    }
    setShowAufgabeEdit(true);
  }, [vorschlag.aufgaben_details]);

  const handleAufgabeErstellen = useCallback(async () => {
    const details = showAufgabeEdit && aufgabeForm ? aufgabeForm : vorschlag.aufgaben_details;
    if (!details) return;
    setIsSavingAufgabe(true);
    try {
      const ok = await onAufgabeErstellen(vorschlag.id, details);
      if (ok) {
        toast.success('Aufgabe erstellt');
      } else {
        toast.error('Aufgabe konnte nicht erstellt werden');
      }
    } finally {
      setIsSavingAufgabe(false);
    }
  }, [showAufgabeEdit, aufgabeForm, vorschlag.aufgaben_details, vorschlag.id, onAufgabeErstellen]);

  // Issue proposal panel state
  const [showIssueEdit, setShowIssueEdit] = useState(false);
  const [issueForm, setIssueForm] = useState<IssueDetails | null>(null);
  const [showFehlerLog, setShowFehlerLog] = useState(false);
  const [showTechnisch, setShowTechnisch] = useState(false);
  const [isSavingIssue, setIsSavingIssue] = useState(false);

  const handleIssueBearbeiten = useCallback(() => {
    if (vorschlag.issue_details) {
      setIssueForm({ ...vorschlag.issue_details });
    }
    setShowIssueEdit(true);
  }, [vorschlag.issue_details]);

  const handleIssueInRoadmap = useCallback(async () => {
    const details = showIssueEdit && issueForm ? issueForm : vorschlag.issue_details;
    if (!details) return;
    setIsSavingIssue(true);
    try {
      const issueUrl = await onIssueInRoadmap(vorschlag.id, details);
      if (issueUrl) {
        toast.success(
          <span>
            Issue auf GitHub erstellt —{' '}
            <a href={issueUrl} target="_blank" rel="noreferrer" className="underline">
              öffnen
            </a>
          </span>
        );
      } else {
        toast.error('GitHub Issue konnte nicht erstellt werden');
      }
    } finally {
      setIsSavingIssue(false);
    }
  }, [showIssueEdit, issueForm, vorschlag.issue_details, vorschlag.id, onIssueInRoadmap]);

  // Feedback-History lazy laden
  const loadKontext = useCallback(async () => {
    if (kontextLoaded || kontextLoading) return;
    setKontextLoading(true);
    try {
      const data = await loadKontextForVorschlag(vorschlag.id);
      setKontextEintraege(data);
      setKontextLoaded(true);
    } catch (err) {
      console.error('Kontext laden:', err);
    } finally {
      setKontextLoading(false);
    }
  }, [vorschlag.id, kontextLoaded, kontextLoading]);

  const toggleKontext = useCallback(() => {
    if (!showKontext && !kontextLoaded) {
      loadKontext();
    }
    setShowKontext(prev => !prev);
  }, [showKontext, kontextLoaded, loadKontext]);

  const typKey = vorschlag.vorschlag_typ || vorschlag.typ || 'allgemein';
  const typConfig = VORSCHLAG_TYP_CONFIG[typKey] || VORSCHLAG_TYP_CONFIG.allgemein;
  const TypIcon = typConfig.icon;

  // E-Mail-Meta aus dem Kontext extrahieren
  const emailMeta = vorschlag.meta || {};
  const istEmail = typKey === 'email' || emailMeta.email_absender || emailMeta.email_betreff;
  const emailAbsender = emailMeta.email_absender || emailMeta.absender || emailMeta.from;
  const emailBetreff = emailMeta.email_betreff || emailMeta.betreff || emailMeta.subject;
  const emailVorschau = emailMeta.email_vorschau || emailMeta.vorschau || emailMeta.snippet;
  const hatAnhaenge = emailMeta.anhaenge_count > 0 || emailMeta.has_attachments;
  const agentName = emailMeta.agent_name || vorschlag.erstellt_von;

  // Vorgeschlagene Aktionen aus dem Vorschlag oder Standard
  const vorgeschlageneAktionen = (vorschlag.meta?.vorgeschlagene_aktionen || []) as { typ: string; label: string; text?: string; zeit?: string }[];

  const handleEntscheidung = async (entscheidung: Entscheidung) => {
    if (entscheidung === 'verbesserung') {
      setShowVerbesserung(true);
      setShowAktionen(false);
      setShowAnweisung(false);
      return;
    }
    const ok = await onEntscheiden(vorschlag.id, entscheidung);
    if (ok) toast.success(entscheidung === 'ja' ? 'Genehmigt' : 'Abgelehnt');
  };

  const handleVerbesserungSenden = async () => {
    if (!verbesserungText.trim()) return;
    const ok = await onEntscheiden(vorschlag.id, 'verbesserung', verbesserungText.trim());
    if (ok) {
      toast.success('Verbesserung gespeichert');
      setShowVerbesserung(false);
      setVerbesserungText('');
    }
  };

  const handleAktionAusfuehren = async () => {
    if (!gewaehlteAktion) return;
    const details: Record<string, any> = {
      aktion: gewaehlteAktion,
      anweisung: anweisungText.trim() || undefined,
      geplante_zeit: antwortZeit || undefined,
      ziel_agent: zielAgent || undefined,
    };
    const ok = await onAktion(vorschlag.id, gewaehlteAktion, details);
    if (ok) {
      toast.success(`Aktion "${VORSCHLAG_AKTIONEN.find(a => a.id === gewaehlteAktion)?.label}" gespeichert`);
      setShowAktionen(false);
      setGewaehlteAktion(null);
      setAnweisungText('');
      setAntwortZeit('');
      setZielAgent('');
    }
  };

  const handleAnweisungSenden = async () => {
    if (!anweisungText.trim()) return;
    const ok = await onAktion(vorschlag.id, 'anweisung', {
      aktion: 'anweisung',
      anweisung: anweisungText.trim(),
      ziel_agent: zielAgent || undefined,
    });
    if (ok) {
      toast.success('Anweisung gespeichert — wird beim nächsten Durchlauf gelesen');
      setShowAnweisung(false);
      setAnweisungText('');
      setZielAgent('');
    }
  };

  const istEntschieden = !!vorschlag.entscheidung;
  const zebraBg = index % 2 === 0 ? CI.bgZebraEven : CI.bgZebraOdd;

  return (
    <div
      className={`rounded-xl border transition-all duration-200 ${
        istEntschieden
          ? 'opacity-60'
          : vorschlag.gelesen
            ? 'hover:shadow-md'
            : 'shadow-sm'
      }`}
      style={{
        backgroundColor: istEntschieden ? CI.bgSection : zebraBg,
        borderColor: istEntschieden ? CI.borderLight : vorschlag.gelesen ? CI.border : CI.redBorder,
        boxShadow: !istEntschieden && !vorschlag.gelesen ? CI.shadow : undefined,
      }}
    >
      <div className="p-3 sm:p-4">
        {/* Header */}
        <div className="flex items-start gap-2.5 sm:gap-3">
          <div
            className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: typConfig.bg }}
          >
            <TypIcon className="w-4.5 h-4.5" style={{ color: typConfig.color }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold" style={{ color: CI.textPrimary }}>{vorschlag.titel}</span>
              <span
                className="text-[10px] font-medium px-2 py-0.5 rounded-full"
                style={{ backgroundColor: typConfig.bg, color: typConfig.color }}
              >
                {typConfig.label}
              </span>
              {emailMeta.prioritaet === 'hoch' && (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: CI.redLight, color: CI.red }}>DRINGEND</span>
              )}
            </div>

            {/* Agent-Info */}
            {agentName && (
              <div className="flex items-center gap-1.5 mt-1">
                <Bot className="w-3 h-3" style={{ color: CI.textMuted }} />
                <span className="text-[10px]" style={{ color: CI.textMuted }}>{agentName}</span>
              </div>
            )}

            {/* E-Mail-Kontext (wenn vorhanden) */}
            {istEmail && (emailAbsender || emailBetreff) && (
              <div className="mt-2 px-3 py-2.5 rounded-lg" style={{ backgroundColor: CI.redLight, border: `1px solid ${CI.redBorder}` }}>
                <div className="flex items-center gap-2 mb-1">
                  <Mail className="w-3.5 h-3.5" style={{ color: CI.red }} />
                  <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: CI.redDark }}>E-Mail</span>
                </div>
                {emailAbsender && (
                  <p className="text-[11px] font-medium" style={{ color: CI.textPrimary }}>
                    Von: {emailAbsender}
                  </p>
                )}
                {emailBetreff && (
                  <p className="text-[11px]" style={{ color: CI.textSecondary }}>
                    Betreff: {emailBetreff}
                  </p>
                )}
                {emailVorschau && (
                  <p className="text-[10px] mt-1 leading-relaxed line-clamp-2" style={{ color: CI.textMuted }}>
                    {emailVorschau}
                  </p>
                )}
                {hatAnhaenge && (
                  <div className="flex items-center gap-1 mt-1.5">
                    <FolderOpen className="w-3 h-3" style={{ color: CI.purple }} />
                    <span className="text-[9px] font-medium" style={{ color: CI.purple }}>
                      {emailMeta.anhaenge_count || '?'} Anhänge
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Beschreibung (wenn keine E-Mail oder zusätzlich) */}
            {vorschlag.inhalt && !istEmail && (
              <p className="text-xs mt-1.5 leading-relaxed" style={{ color: CI.textSecondary }}>{vorschlag.inhalt}</p>
            )}
            {vorschlag.inhalt && istEmail && (
              <p className="text-xs mt-2 leading-relaxed" style={{ color: CI.textSecondary }}>{vorschlag.inhalt}</p>
            )}

            {/* KI-Entwurf */}
            {vorschlag.vorschlag_text && (
              <div className="mt-3 px-3.5 py-3 rounded-lg" style={{ backgroundColor: CI.amberBg, border: `1px solid #FDE68A` }}>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Sparkles className="w-3.5 h-3.5" style={{ color: CI.amber }} />
                  <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#92400E' }}>KI-Entwurf</span>
                </div>
                <p className="text-xs whitespace-pre-wrap leading-relaxed" style={{ color: '#78350F' }}>{vorschlag.vorschlag_text}</p>
              </div>
            )}

            {/* Vorgeschlagene Aktionen vom Agenten */}
            {vorgeschlageneAktionen.length > 0 && !istEntschieden && (
              <div className="mt-2.5 space-y-1.5">
                <span className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: CI.textMuted }}>Vorgeschlagene Aktionen</span>
                <div className="flex flex-wrap gap-1.5">
                  {vorgeschlageneAktionen.map((va, i) => {
                    const aktConf = VORSCHLAG_AKTIONEN.find(a => a.id === va.typ);
                    const AktIcon = aktConf?.icon || Zap;
                    return (
                      <button
                        key={i}
                        onClick={() => {
                          setGewaehlteAktion(va.typ);
                          if (va.text) setAnweisungText(va.text);
                          if (va.zeit) setAntwortZeit(va.zeit);
                          setShowAktionen(true);
                          setShowVerbesserung(false);
                          setShowAnweisung(false);
                        }}
                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-medium transition-all active:scale-95"
                        style={{ backgroundColor: aktConf?.bg || CI.bgSection, color: aktConf?.color || CI.textSecondary, border: `1px solid ${CI.border}` }}
                      >
                        <AktIcon className="w-3 h-3" />
                        {va.label || aktConf?.label}
                        {va.zeit && <span className="ml-1 opacity-70">({va.zeit})</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <span className="text-[10px] flex-shrink-0 mt-1" style={{ color: CI.textMuted }}>{zeitAgo(vorschlag.erstellt_am)}</span>
        </div>

        {/* Entscheidung anzeigen */}
        {istEntschieden && (
          <div className={`mt-3 ml-0 sm:ml-12 px-3 sm:px-3.5 py-2.5 rounded-lg border ${
            vorschlag.entscheidung === 'ja'
              ? 'bg-emerald-50 border-emerald-100'
              : vorschlag.entscheidung === 'nein'
                ? 'bg-red-50 border-red-100'
                : 'bg-blue-50 border-blue-100'
          }`}>
            <div className="flex items-center gap-2">
              {vorschlag.entscheidung === 'ja' && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />}
              {vorschlag.entscheidung === 'nein' && <XCircle className="w-3.5 h-3.5 text-red-600" />}
              {vorschlag.entscheidung === 'verbesserung' && <Edit3 className="w-3.5 h-3.5 text-blue-600" />}
              <span className={`text-xs font-medium ${
                vorschlag.entscheidung === 'ja' ? 'text-emerald-700' :
                vorschlag.entscheidung === 'nein' ? 'text-red-700' : 'text-blue-700'
              }`}>
                {vorschlag.entscheidung === 'ja' ? 'Genehmigt' :
                 vorschlag.entscheidung === 'nein' ? 'Abgelehnt' : 'Verbessert'}
              </span>
              {vorschlag.antwort_datum && (
                <span className="text-[10px] ml-auto" style={{ color: CI.textMuted }}>
                  {new Date(vorschlag.antwort_datum).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </div>
            {vorschlag.verbesserung_text && (
              <p className="text-xs text-blue-800/80 mt-1.5">{vorschlag.verbesserung_text}</p>
            )}
          </div>
        )}

        {/* ═══ FEEDBACK-HISTORY / KONTEXT-VERLAUF ═══ */}
        <div className="mt-2 ml-0 sm:ml-12">
          <button
            onClick={toggleKontext}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-medium transition-all active:scale-95"
            style={{
              color: showKontext ? CI.purple : CI.textMuted,
              backgroundColor: showKontext ? CI.purpleBg : 'transparent',
              border: `1px solid ${showKontext ? CI.purple : 'transparent'}`,
            }}
          >
            <History className="w-3 h-3" />
            {kontextLoading ? 'Lädt...' : showKontext ? 'Verlauf ausblenden' : 'Verlauf & Kontext'}
            {kontextLoaded && kontextEintraege.length > 0 && (
              <span
                className="ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold"
                style={{ backgroundColor: CI.purpleBg, color: CI.purple }}
              >
                {kontextEintraege.length}
              </span>
            )}
            {showKontext ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>

          {showKontext && (
            <div className="mt-2 space-y-0 rounded-lg overflow-hidden" style={{ border: `1px solid ${CI.border}` }}>
              {kontextLoading && (
                <div className="flex items-center justify-center py-6" style={{ backgroundColor: CI.bgSection }}>
                  <Loader2 className="w-4 h-4 animate-spin" style={{ color: CI.purple }} />
                  <span className="text-[10px] ml-2" style={{ color: CI.textMuted }}>Verlauf wird geladen...</span>
                </div>
              )}

              {kontextLoaded && kontextEintraege.length === 0 && (
                <div className="flex items-center justify-center py-4" style={{ backgroundColor: CI.bgSection }}>
                  <span className="text-[10px]" style={{ color: CI.textMuted }}>Noch kein Verlauf vorhanden</span>
                </div>
              )}

              {kontextLoaded && kontextEintraege.length > 0 && (
                <div>
                  {/* Timeline-Header */}
                  <div className="px-3 py-2 flex items-center gap-2" style={{ backgroundColor: CI.purpleBg, borderBottom: `1px solid ${CI.border}` }}>
                    <History className="w-3.5 h-3.5" style={{ color: CI.purple }} />
                    <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: CI.purple }}>Verlauf ({kontextEintraege.length})</span>
                  </div>

                  {/* Timeline-Einträge */}
                  {kontextEintraege.map((eintrag, idx) => {
                    const istFeedback = eintrag.typ === 'feedback';
                    const istLetzter = idx === kontextEintraege.length - 1;

                    // Icon und Farbe basierend auf Typ
                    const getKontextStyle = () => {
                      if (eintrag.icon_typ === 'angenommen') return { icon: CheckCircle2, color: CI.emerald, bg: CI.emeraldBg };
                      if (eintrag.icon_typ === 'abgelehnt') return { icon: XCircle, color: CI.red, bg: CI.redLight };
                      if (eintrag.icon_typ === 'verbessert') return { icon: Edit3, color: CI.blue, bg: CI.blueBg };
                      if (eintrag.icon_typ === 'aktion') return { icon: Zap, color: CI.amber, bg: CI.amberBg };
                      if (eintrag.icon_typ === 'anweisung') return { icon: Lightbulb, color: CI.amber, bg: CI.amberBg };
                      return { icon: Activity, color: CI.textMuted, bg: CI.bgSection };
                    };

                    const style = getKontextStyle();
                    const KontextIcon = style.icon;
                    const zebraBgKontext = idx % 2 === 0 ? CI.bgZebraEven : CI.bgZebraOdd;

                    return (
                      <div
                        key={eintrag.id}
                        className="relative"
                        style={{
                          backgroundColor: zebraBgKontext,
                          borderBottom: !istLetzter ? `1px solid ${CI.borderLight}` : undefined,
                        }}
                      >
                        <div className="flex gap-2.5 px-3 py-2.5">
                          {/* Timeline-Linie + Icon */}
                          <div className="flex flex-col items-center flex-shrink-0">
                            <div
                              className="w-6 h-6 rounded-full flex items-center justify-center"
                              style={{ backgroundColor: style.bg }}
                            >
                              <KontextIcon className="w-3 h-3" style={{ color: style.color }} />
                            </div>
                            {!istLetzter && (
                              <div className="w-px flex-1 mt-1" style={{ backgroundColor: CI.border }} />
                            )}
                          </div>

                          {/* Inhalt */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-[11px] font-semibold" style={{ color: style.color }}>
                                {eintrag.titel}
                              </span>
                              <span
                                className="text-[9px] px-1.5 py-0.5 rounded-full font-medium"
                                style={{
                                  backgroundColor: istFeedback ? CI.purpleBg : CI.cyanBg,
                                  color: istFeedback ? CI.purple : CI.cyan,
                                }}
                              >
                                {istFeedback ? 'Entscheidung' : 'System-Aktion'}
                              </span>
                              <span className="text-[9px] ml-auto" style={{ color: CI.textMuted }}>
                                {zeitAgo(eintrag.datum)}
                              </span>
                            </div>

                            {/* Beschreibung */}
                            {eintrag.beschreibung && (
                              <p className="text-[10px] mt-1 leading-relaxed line-clamp-3" style={{ color: CI.textSecondary }}>
                                {eintrag.beschreibung}
                              </p>
                            )}

                            {/* Details: Ziel-Agent, Aktion, Geplante Zeit */}
                            {eintrag.details && (
                              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                {eintrag.details.gewaehlte_aktion && (
                                  <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ backgroundColor: CI.amberBg, color: CI.amber }}>
                                    Aktion: {eintrag.details.gewaehlte_aktion}
                                  </span>
                                )}
                                {eintrag.details.ziel_agent_id && (
                                  <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ backgroundColor: CI.cyanBg, color: CI.cyan }}>
                                    → {ROUTING_AGENTEN.find(a => a.id === eintrag.details?.ziel_agent_id)?.label || eintrag.details.ziel_agent_id}
                                  </span>
                                )}
                                {eintrag.details.geplante_zeit && (
                                  <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ backgroundColor: CI.blueBg, color: CI.blue }}>
                                    ⏰ {new Date(eintrag.details.geplante_zeit).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── TASK SUGGESTION PANEL ── */}
        {vorschlag.aufgaben_details && (
          <div
            className="rounded-xl border p-3 mt-2"
            style={{ borderColor: CI.blue + '40', background: CI.blueBg }}
          >
            <div className="flex items-center gap-2 mb-2">
              <ClipboardList className="w-4 h-4 shrink-0" style={{ color: CI.blue }} />
              <span className="text-xs font-semibold" style={{ color: CI.blue }}>
                {showAufgabeEdit ? 'Aufgabe bearbeiten' : 'Aufgabe vorgeschlagen'}
              </span>
            </div>

            {!showAufgabeEdit ? (
              <div className="space-y-0.5 mb-2">
                <p className="text-xs" style={{ color: CI.textPrimary }}>
                  <span className="font-medium">Titel: </span>{vorschlag.aufgaben_details.titel}
                </p>
                <p className="text-xs" style={{ color: CI.textSecondary }}>
                  <span className="font-medium">Priorität: </span>
                  {vorschlag.aufgaben_details.prioritaet}
                  {vorschlag.aufgaben_details.faellig_am && (
                    <> · <span className="font-medium">Fällig: </span>
                      {new Date(vorschlag.aufgaben_details.faellig_am).toLocaleDateString('de-DE')}
                    </>
                  )}
                </p>
                {assigneeName && (
                  <p className="text-xs" style={{ color: CI.textSecondary }}>
                    <span className="font-medium">Zugewiesen: </span>{assigneeName}
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-2 mb-2">
                <input
                  type="text"
                  value={aufgabeForm?.titel ?? ''}
                  onChange={e => setAufgabeForm(f => f ? { ...f, titel: e.target.value } : f)}
                  placeholder="Titel"
                  className="w-full text-xs rounded-lg px-2 py-1.5 border focus:outline-none"
                  style={{ borderColor: CI.border, background: CI.bgPanel }}
                />
                <textarea
                  value={aufgabeForm?.beschreibung ?? ''}
                  onChange={e => setAufgabeForm(f => f ? { ...f, beschreibung: e.target.value } : f)}
                  placeholder="Beschreibung"
                  rows={2}
                  className="w-full text-xs rounded-lg px-2 py-1.5 border focus:outline-none resize-none"
                  style={{ borderColor: CI.border, background: CI.bgPanel }}
                />
                <div className="flex gap-2">
                  <select
                    value={aufgabeForm?.prioritaet ?? 'mittel'}
                    onChange={e => setAufgabeForm(f => f ? { ...f, prioritaet: e.target.value as AufgabenDetails['prioritaet'] } : f)}
                    className="flex-1 text-xs rounded-lg px-2 py-1.5 border focus:outline-none"
                    style={{ borderColor: CI.border, background: CI.bgPanel }}
                  >
                    <option value="niedrig">Niedrig</option>
                    <option value="mittel">Mittel</option>
                    <option value="hoch">Hoch</option>
                    <option value="dringend">Dringend</option>
                  </select>
                  <input
                    type="date"
                    value={aufgabeForm?.faellig_am ?? ''}
                    onChange={e => setAufgabeForm(f => f ? { ...f, faellig_am: e.target.value } : f)}
                    className="flex-1 text-xs rounded-lg px-2 py-1.5 border focus:outline-none"
                    style={{ borderColor: CI.border, background: CI.bgPanel }}
                  />
                </div>
                <div className="flex gap-2">
                  <select
                    value={aufgabeForm?.zugewiesen_an ?? '11111111-1111-1111-1111-111111111111'}
                    onChange={e => setAufgabeForm(f => f ? { ...f, zugewiesen_an: e.target.value } : f)}
                    className="flex-1 text-xs rounded-lg px-2 py-1.5 border focus:outline-none"
                    style={{ borderColor: CI.border, background: CI.bgPanel }}
                  >
                    <option value="11111111-1111-1111-1111-111111111111">Andreas Koop</option>
                    {mitarbeiter
                      .filter(m => m.id !== '11111111-1111-1111-1111-111111111111')
                      .map(m => (
                        <option key={m.id} value={m.id}>{m.vorname} {m.nachname}</option>
                      ))
                    }
                  </select>
                  <select
                    value={aufgabeForm?.business_unit ?? 'gutachten'}
                    onChange={e => setAufgabeForm(f => f ? { ...f, business_unit: e.target.value as AufgabenDetails['business_unit'] } : f)}
                    className="flex-1 text-xs rounded-lg px-2 py-1.5 border focus:outline-none"
                    style={{ borderColor: CI.border, background: CI.bgPanel }}
                  >
                    <option value="gutachten">Gutachten</option>
                    <option value="autoglas">Autoglas</option>
                    <option value="mietwagen">Mietwagen</option>
                    <option value="werkstatt">Werkstatt</option>
                  </select>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2">
              {showAufgabeEdit ? (
                <button
                  onClick={() => setShowAufgabeEdit(false)}
                  className="px-3 py-1 text-xs rounded-lg border transition-colors"
                  style={{ borderColor: CI.border, color: CI.textSecondary }}
                >
                  Abbrechen
                </button>
              ) : (
                <button
                  onClick={handleAufgabeBearbeiten}
                  className="px-3 py-1 text-xs rounded-lg border transition-colors"
                  style={{ borderColor: CI.border, color: CI.textSecondary }}
                >
                  Bearbeiten
                </button>
              )}
              <button
                onClick={handleAufgabeErstellen}
                disabled={isSavingAufgabe || isSaving}
                className="flex items-center gap-1.5 px-3 py-1 text-xs rounded-lg font-semibold transition-colors disabled:opacity-50"
                style={{ background: CI.blue, color: '#fff' }}
              >
                {isSavingAufgabe ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                Aufgabe erstellen
              </button>
            </div>
          </div>
        )}

        {/* ── ISSUE PROPOSAL PANEL ── */}
        {vorschlag.issue_details && (
          <div
            className="rounded-xl border p-3 mt-2"
            style={{ borderColor: CI.amber + '60', background: CI.amberBg }}
          >
            <div className="flex items-center gap-2 mb-2">
              <Map className="w-4 h-4 shrink-0" style={{ color: CI.amber }} />
              <span className="text-xs font-semibold" style={{ color: CI.amber }}>
                {showIssueEdit ? 'Issue bearbeiten' : 'Roadmap-Issue vorgeschlagen'}
              </span>
            </div>

            {!showIssueEdit ? (
              <div className="space-y-1 mb-2">
                <p className="text-xs font-medium" style={{ color: CI.textPrimary }}>
                  {vorschlag.issue_details.titel}
                </p>
                {/* Kurzbeschreibung: für nicht-technische Nutzer, immer sichtbar */}
                {vorschlag.issue_details.kurzbeschreibung ? (
                  <p className="text-xs" style={{ color: CI.textSecondary }}>
                    {vorschlag.issue_details.kurzbeschreibung}
                  </p>
                ) : (
                  /* Fallback für ältere Vorschläge ohne kurzbeschreibung */
                  <p className="text-xs" style={{ color: CI.textSecondary }}>
                    {vorschlag.issue_details.beschreibung}
                  </p>
                )}
                {/* Technische Details: nur bei kurzbeschreibung vorhanden expandierbar */}
                {vorschlag.issue_details.kurzbeschreibung && (
                  <>
                    <button
                      onClick={() => setShowTechnisch(s => !s)}
                      className="flex items-center gap-1 text-xs mt-0.5"
                      style={{ color: CI.textMuted }}
                    >
                      <ChevronDown className={`w-3 h-3 transition-transform ${showTechnisch ? 'rotate-180' : ''}`} />
                      Technische Details {showTechnisch ? 'ausblenden' : 'anzeigen'}
                    </button>
                    {showTechnisch && (
                      <div className="space-y-1 mt-1 pt-1" style={{ borderTop: `1px solid ${CI.border}` }}>
                        <p className="text-xs" style={{ color: CI.textSecondary }}>
                          {vorschlag.issue_details.beschreibung}
                        </p>
                        <div className="flex flex-wrap items-center gap-1.5 mt-1">
                          {vorschlag.issue_details.labels.map(label => (
                            <span
                              key={label}
                              className="px-1.5 py-0.5 rounded text-xs"
                              style={{ background: CI.border, color: CI.textSecondary }}
                            >
                              {label}
                            </span>
                          ))}
                          <span className="text-xs ml-auto" style={{ color: CI.textMuted }}>
                            Priorität: {vorschlag.issue_details.prioritaet}
                          </span>
                        </div>
                        {vorschlag.issue_details.fehler_log && (
                          <button
                            onClick={() => setShowFehlerLog(s => !s)}
                            className="flex items-center gap-1 text-xs mt-0.5"
                            style={{ color: CI.textMuted }}
                          >
                            <ChevronDown className={`w-3 h-3 transition-transform ${showFehlerLog ? 'rotate-180' : ''}`} />
                            Fehler-Log {showFehlerLog ? 'ausblenden' : 'anzeigen'}
                          </button>
                        )}
                        {showFehlerLog && vorschlag.issue_details.fehler_log && (
                          <pre
                            className="text-xs p-2 rounded-lg overflow-x-auto mt-1"
                            style={{ background: CI.bgSection, color: CI.textSecondary, fontFamily: 'monospace', maxHeight: 120 }}
                          >
                            {vorschlag.issue_details.fehler_log}
                          </pre>
                        )}
                      </div>
                    )}
                  </>
                )}
                {/* Fallback: labels + priority + Fehler-Log direkt (kein kurzbeschreibung) */}
                {!vorschlag.issue_details.kurzbeschreibung && (
                  <>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1">
                      {vorschlag.issue_details.labels.map(label => (
                        <span
                          key={label}
                          className="px-1.5 py-0.5 rounded text-xs"
                          style={{ background: CI.border, color: CI.textSecondary }}
                        >
                          {label}
                        </span>
                      ))}
                      <span className="text-xs ml-auto" style={{ color: CI.textMuted }}>
                        Priorität: {vorschlag.issue_details.prioritaet}
                      </span>
                    </div>
                    {vorschlag.issue_details.fehler_log && (
                      <button
                        onClick={() => setShowFehlerLog(s => !s)}
                        className="flex items-center gap-1 text-xs mt-1"
                        style={{ color: CI.textMuted }}
                      >
                        <ChevronDown className={`w-3 h-3 transition-transform ${showFehlerLog ? 'rotate-180' : ''}`} />
                        Fehler-Log {showFehlerLog ? 'ausblenden' : 'anzeigen'}
                      </button>
                    )}
                    {showFehlerLog && vorschlag.issue_details.fehler_log && (
                      <pre
                        className="text-xs p-2 rounded-lg overflow-x-auto mt-1"
                        style={{ background: CI.bgSection, color: CI.textSecondary, fontFamily: 'monospace', maxHeight: 120 }}
                      >
                        {vorschlag.issue_details.fehler_log}
                      </pre>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-2 mb-2">
                <input
                  type="text"
                  value={issueForm?.titel ?? ''}
                  onChange={e => setIssueForm(f => f ? { ...f, titel: e.target.value } : f)}
                  placeholder="Issue-Titel"
                  className="w-full text-xs rounded-lg px-2 py-1.5 border focus:outline-none"
                  style={{ borderColor: CI.border, background: CI.bgPanel }}
                />
                <textarea
                  value={issueForm?.kurzbeschreibung ?? ''}
                  onChange={e => setIssueForm(f => f ? { ...f, kurzbeschreibung: e.target.value } : f)}
                  placeholder="Kurzbeschreibung (für Admin, nicht-technisch)"
                  rows={2}
                  className="w-full text-xs rounded-lg px-2 py-1.5 border focus:outline-none resize-none"
                  style={{ borderColor: CI.border, background: CI.bgPanel }}
                />
                <textarea
                  value={issueForm?.beschreibung ?? ''}
                  onChange={e => setIssueForm(f => f ? { ...f, beschreibung: e.target.value } : f)}
                  placeholder="Technische Beschreibung (für Entwickler)"
                  rows={3}
                  className="w-full text-xs rounded-lg px-2 py-1.5 border focus:outline-none resize-none"
                  style={{ borderColor: CI.border, background: CI.bgPanel }}
                />
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={issueForm?.labels.join(', ') ?? ''}
                    onChange={e => setIssueForm(f => f ? { ...f, labels: e.target.value.split(',').map(l => l.trim()).filter(Boolean) } : f)}
                    placeholder="Labels: bug, agent-system"
                    className="flex-1 text-xs rounded-lg px-2 py-1.5 border focus:outline-none"
                    style={{ borderColor: CI.border, background: CI.bgPanel }}
                  />
                  <select
                    value={issueForm?.prioritaet ?? 'mittel'}
                    onChange={e => setIssueForm(f => f ? { ...f, prioritaet: e.target.value as IssueDetails['prioritaet'] } : f)}
                    className="text-xs rounded-lg px-2 py-1.5 border focus:outline-none"
                    style={{ borderColor: CI.border, background: CI.bgPanel }}
                  >
                    <option value="niedrig">Niedrig</option>
                    <option value="mittel">Mittel</option>
                    <option value="hoch">Hoch</option>
                  </select>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2">
              {showIssueEdit ? (
                <button
                  onClick={() => setShowIssueEdit(false)}
                  className="px-3 py-1 text-xs rounded-lg border transition-colors"
                  style={{ borderColor: CI.border, color: CI.textSecondary }}
                >
                  Abbrechen
                </button>
              ) : (
                <button
                  onClick={handleIssueBearbeiten}
                  className="px-3 py-1 text-xs rounded-lg border transition-colors"
                  style={{ borderColor: CI.border, color: CI.textSecondary }}
                >
                  Bearbeiten
                </button>
              )}
              <button
                onClick={handleIssueInRoadmap}
                disabled={isSavingIssue || isSaving}
                className="flex items-center gap-1.5 px-3 py-1 text-xs rounded-lg font-semibold transition-colors disabled:opacity-50"
                style={{ background: CI.amber, color: '#fff' }}
              >
                {isSavingIssue ? <Loader2 className="w-3 h-3 animate-spin" /> : <Github className="w-3 h-3" />}
                In Roadmap →
              </button>
            </div>
          </div>
        )}

        {/* ═══ AKTIONS-LEISTE (Hauptbuttons) ═══ */}
        {!istEntschieden && !showVerbesserung && !showAktionen && !showAnweisung && (
          <div className="mt-3 sm:mt-3.5 ml-0 sm:ml-12 space-y-2">
            {/* Primäre Entscheidung */}
            <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
              <button
                onClick={() => handleEntscheidung('ja')}
                disabled={isSaving}
                className="flex items-center gap-1.5 px-3.5 sm:px-4 py-2.5 sm:py-2 text-white text-xs font-medium rounded-lg disabled:opacity-50 transition-all active:scale-95 min-h-[44px] sm:min-h-0"
                style={{ backgroundColor: CI.emerald }}
              >
                {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Genehmigen
              </button>
              <button
                onClick={() => handleEntscheidung('nein')}
                disabled={isSaving}
                className="flex items-center gap-1.5 px-3.5 sm:px-4 py-2.5 sm:py-2 text-xs font-medium rounded-lg border disabled:opacity-50 transition-all active:scale-95 min-h-[44px] sm:min-h-0"
                style={{ color: CI.red, borderColor: CI.redBorder, backgroundColor: CI.bgPanel }}
              >
                <X className="w-3.5 h-3.5" />
                Ablehnen
              </button>
              <button
                onClick={() => handleEntscheidung('verbesserung')}
                disabled={isSaving}
                className="flex items-center gap-1.5 px-3.5 sm:px-4 py-2.5 sm:py-2 text-xs font-medium rounded-lg border disabled:opacity-50 transition-all active:scale-95 min-h-[44px] sm:min-h-0"
                style={{ color: CI.blue, borderColor: '#BFDBFE', backgroundColor: CI.bgPanel }}
              >
                <Edit3 className="w-3.5 h-3.5" />
                Verbessern
              </button>
            </div>

            {/* Sekundäre Aktionen */}
            <div className="flex items-center gap-1.5 flex-wrap mt-1">
              {VORSCHLAG_AKTIONEN.map(aktion => {
                const AktIcon = aktion.icon;
                return (
                  <button
                    key={aktion.id}
                    onClick={() => {
                      setGewaehlteAktion(aktion.id);
                      setShowAktionen(true);
                      setShowVerbesserung(false);
                      setShowAnweisung(false);
                    }}
                    className="flex items-center gap-1 px-2.5 py-2 sm:py-1.5 rounded-lg text-[10px] font-medium transition-all active:scale-95 border min-h-[40px] sm:min-h-0"
                    style={{ color: aktion.color, backgroundColor: CI.bgPanel, borderColor: CI.border }}
                  >
                    <AktIcon className="w-3.5 sm:w-3 h-3.5 sm:h-3" />
                    {aktion.label}
                  </button>
                );
              })}
              <button
                onClick={() => {
                  setShowAnweisung(true);
                  setShowAktionen(false);
                  setShowVerbesserung(false);
                }}
                className="flex items-center gap-1 px-2.5 py-2 sm:py-1.5 rounded-lg text-[10px] font-medium transition-all active:scale-95 border min-h-[40px] sm:min-h-0"
                style={{ color: CI.amber, backgroundColor: CI.bgPanel, borderColor: CI.border }}
              >
                <Lightbulb className="w-3.5 sm:w-3 h-3.5 sm:h-3" />
                Anweisung
              </button>
            </div>
          </div>
        )}

        {/* ═══ AKTIONS-DETAIL-PANEL ═══ */}
        {showAktionen && !istEntschieden && (
          <div className="mt-3 sm:mt-3.5 ml-0 sm:ml-12 space-y-3 p-3 sm:p-3.5 rounded-lg" style={{ backgroundColor: CI.bgSection, border: `1px solid ${CI.border}` }}>
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4" style={{ color: CI.amber }} />
              <span className="text-xs font-semibold" style={{ color: CI.textPrimary }}>Aktion konfigurieren</span>
            </div>

            {/* Aktions-Auswahl */}
            <div className="flex flex-wrap gap-1.5">
              {VORSCHLAG_AKTIONEN.map(aktion => {
                const AktIcon = aktion.icon;
                const isSelected = gewaehlteAktion === aktion.id;
                return (
                  <button
                    key={aktion.id}
                    onClick={() => setGewaehlteAktion(aktion.id)}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-medium transition-all"
                    style={{
                      backgroundColor: isSelected ? aktion.bg : CI.bgPanel,
                      color: isSelected ? aktion.color : CI.textMuted,
                      border: `1px solid ${isSelected ? aktion.color : CI.border}`,
                      fontWeight: isSelected ? 600 : 400,
                    }}
                  >
                    <AktIcon className="w-3 h-3" />
                    {aktion.label}
                  </button>
                );
              })}
            </div>

            {/* Zeitplanung (bei Antworten) */}
            {(gewaehlteAktion === 'antworten' || gewaehlteAktion === 'aufgabe') && (
              <div className="flex items-center gap-2 flex-wrap">
                <CalendarClock className="w-3.5 h-3.5" style={{ color: CI.textMuted }} />
                <span className="text-[10px]" style={{ color: CI.textMuted }}>Wann?</span>
                <input
                  type="time"
                  value={antwortZeit}
                  onChange={e => setAntwortZeit(e.target.value)}
                  className="text-[11px] px-2 py-1 rounded-lg border focus:outline-none focus:ring-1"
                  style={{ borderColor: CI.border, color: CI.textPrimary }}
                />
                <button
                  onClick={() => setAntwortZeit('09:00')}
                  className="text-[9px] px-2 py-1 rounded-lg"
                  style={{ backgroundColor: CI.bgPanel, color: CI.textMuted, border: `1px solid ${CI.border}` }}
                >Morgens</button>
                <button
                  onClick={() => setAntwortZeit('14:00')}
                  className="text-[9px] px-2 py-1 rounded-lg"
                  style={{ backgroundColor: CI.bgPanel, color: CI.textMuted, border: `1px solid ${CI.border}` }}
                >Nachmittags</button>
                <button
                  onClick={() => setAntwortZeit('')}
                  className="text-[9px] px-2 py-1 rounded-lg"
                  style={{ backgroundColor: CI.emeraldBg, color: CI.emerald, border: `1px solid #A7F3D0` }}
                >Sofort</button>
              </div>
            )}

            {/* Agent-Routing (bei Weiterleiten) */}
            {gewaehlteAktion === 'weiterleiten' && (
              <div className="space-y-1.5">
                <span className="text-[10px] font-medium" style={{ color: CI.textMuted }}>An welchen Agenten?</span>
                <div className="flex flex-wrap gap-1.5">
                  {ROUTING_AGENTEN.map(ra => {
                    const RAIcon = ra.icon;
                    const isSelected = zielAgent === ra.id;
                    return (
                      <button
                        key={ra.id}
                        onClick={() => setZielAgent(ra.id)}
                        className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[10px] transition-all"
                        style={{
                          backgroundColor: isSelected ? CI.cyanBg : CI.bgPanel,
                          color: isSelected ? CI.cyan : CI.textMuted,
                          border: `1px solid ${isSelected ? CI.cyan : CI.border}`,
                          fontWeight: isSelected ? 600 : 400,
                        }}
                      >
                        <RAIcon className="w-3 h-3" />
                        {ra.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Anweisung / Freitext */}
            <textarea
              value={anweisungText}
              onChange={e => setAnweisungText(e.target.value)}
              placeholder={gewaehlteAktion === 'antworten' ? 'Antworttext oder Anweisung...' : gewaehlteAktion === 'weiterleiten' ? 'Anweisung an den Agenten...' : 'Zusätzliche Anweisung...'}
              rows={2}
              className="w-full px-3 py-2 rounded-lg border text-xs placeholder-gray-400 focus:outline-none focus:ring-2 resize-none"
              style={{ borderColor: CI.border, color: CI.textPrimary }}
            />

            {/* Aktions-Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleAktionAusfuehren}
                disabled={!gewaehlteAktion || isSaving}
                className="flex items-center gap-1.5 px-4 py-2 text-white text-xs font-medium rounded-lg disabled:opacity-50 transition-all active:scale-95"
                style={{ backgroundColor: CI.emerald }}
              >
                {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Ausführen{antwortZeit ? ` um ${antwortZeit}` : ''}
              </button>
              <button
                onClick={() => { setShowAktionen(false); setGewaehlteAktion(null); setAnweisungText(''); setAntwortZeit(''); setZielAgent(''); }}
                className="text-xs px-3 py-2"
                style={{ color: CI.textMuted }}
              >
                Abbrechen
              </button>
            </div>
          </div>
        )}

        {/* ═══ ANWEISUNGS-PANEL ═══ */}
        {showAnweisung && !istEntschieden && (
          <div className="mt-3 sm:mt-3.5 ml-0 sm:ml-12 space-y-3 p-3 sm:p-3.5 rounded-lg" style={{ backgroundColor: CI.amberBg, border: `1px solid #FDE68A` }}>
            <div className="flex items-center gap-2">
              <Lightbulb className="w-4 h-4" style={{ color: CI.amber }} />
              <span className="text-xs font-semibold" style={{ color: '#92400E' }}>Anweisung an das System</span>
            </div>
            <p className="text-[10px] leading-relaxed" style={{ color: '#78350F' }}>
              Diese Anweisung wird beim nächsten Durchlauf vom zuständigen Agenten oder Manus gelesen und berücksichtigt.
            </p>

            {/* Ziel-Agent auswählen */}
            <div className="space-y-1.5">
              <span className="text-[10px] font-medium" style={{ color: '#92400E' }}>An wen?</span>
              <div className="flex flex-wrap gap-1.5">
                {ROUTING_AGENTEN.map(ra => {
                  const RAIcon = ra.icon;
                  const isSelected = zielAgent === ra.id;
                  return (
                    <button
                      key={ra.id}
                      onClick={() => setZielAgent(ra.id)}
                      className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[10px] transition-all"
                      style={{
                        backgroundColor: isSelected ? '#FEF3C7' : CI.bgPanel,
                        color: isSelected ? '#92400E' : CI.textMuted,
                        border: `1px solid ${isSelected ? '#F59E0B' : CI.border}`,
                        fontWeight: isSelected ? 600 : 400,
                      }}
                    >
                      <RAIcon className="w-3 h-3" />
                      {ra.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <textarea
              value={anweisungText}
              onChange={e => setAnweisungText(e.target.value)}
              placeholder="z.B. 'Datenbankstruktur erweitern', 'Rechnung nochmal prüfen', 'Antwort freundlicher formulieren'..."
              rows={3}
              className="w-full px-3 py-2.5 rounded-lg border text-xs placeholder-gray-400 focus:outline-none focus:ring-2 resize-none"
              style={{ borderColor: '#FDE68A', color: CI.textPrimary, backgroundColor: CI.bgPanel }}
              autoFocus
            />

            <div className="flex items-center gap-2">
              <button
                onClick={handleAnweisungSenden}
                disabled={!anweisungText.trim() || isSaving}
                className="flex items-center gap-1.5 px-4 py-2 text-white text-xs font-medium rounded-lg disabled:opacity-50 transition-all active:scale-95"
                style={{ backgroundColor: '#D97706' }}
              >
                {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Anweisung senden
              </button>
              <button
                onClick={() => { setShowAnweisung(false); setAnweisungText(''); setZielAgent(''); }}
                className="text-xs px-3 py-2"
                style={{ color: CI.textMuted }}
              >
                Abbrechen
              </button>
            </div>
          </div>
        )}

        {/* Verbesserung-Eingabe (bestehend) */}
        {showVerbesserung && !istEntschieden && (
          <div className="mt-3.5 ml-12 space-y-2.5">
            <textarea
              value={verbesserungText}
              onChange={e => setVerbesserungText(e.target.value)}
              placeholder="Was soll anders sein?"
              rows={3}
              className="w-full px-3.5 py-2.5 rounded-lg border text-xs placeholder-gray-400 focus:outline-none focus:ring-2 resize-none"
              style={{ borderColor: '#BFDBFE', color: CI.textPrimary }}
              autoFocus
            />
            <div className="flex items-center gap-2">
              <button
                onClick={handleVerbesserungSenden}
                disabled={!verbesserungText.trim() || isSaving}
                className="flex items-center gap-1.5 px-3.5 py-2 text-white text-xs font-medium rounded-lg disabled:opacity-50 transition-all"
                style={{ backgroundColor: CI.blue }}
              >
                {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Absenden
              </button>
              <button
                onClick={() => { setShowVerbesserung(false); setVerbesserungText(''); }}
                className="text-xs px-3 py-2"
                style={{ color: CI.textMuted }}
              >
                Abbrechen
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB: VORSCHLÄGE
// ═══════════════════════════════════════════════════════════════════════════

function VorschlaegeTab({
  vorschlaege,
  vorschlaegeProBereich,
  onEntscheiden,
  onAktion,
  onCreateManual,
  onPrepareStellungnahme,
  onAufgabeErstellen,
  onIssueInRoadmap,
  mitarbeiter,
  saving,
  agenten,
}: {
  vorschlaege: AgentenVorschlag[];
  vorschlaegeProBereich: { bereich: typeof BEREICHE[0]; eintraege: AgentenVorschlag[]; pending: number }[];
  onEntscheiden: (id: string, e: Entscheidung, text?: string) => Promise<boolean>;
  onAktion: (id: string, aktion: string, details: Record<string, any>) => Promise<boolean>;
  onCreateManual: (params: { titel: string; inhalt: string; zielAgent?: string; kategorie?: string; prioritaet?: number; typ?: string }) => Promise<boolean>;
  onPrepareStellungnahme: (vorschlagId: string) => Promise<any>;
  onAufgabeErstellen: (id: string, details: AufgabenDetails) => Promise<boolean>;
  onIssueInRoadmap: (id: string, details: IssueDetails) => Promise<string | null>;
  mitarbeiter: Mitarbeiter[];
  saving: string | null;
  agenten: AgentBriefing[];
}) {
  const [showNeuerVorschlag, setShowNeuerVorschlag] = useState(false);
  const [neuerTitel, setNeuerTitel] = useState('');
  const [neuerInhalt, setNeuerInhalt] = useState('');
  const [neuerZielAgent, setNeuerZielAgent] = useState('');
  const [neueKategorie, setNeueKategorie] = useState('allgemein');
  const [neuePrioritaet, setNeuePrioritaet] = useState(7);
  const [neuerTyp, setNeuerTyp] = useState('manuell');
  const [isSavingManual, setIsSavingManual] = useState(false);

  const [filterStatus, setFilterStatus] = useState<'offen' | 'alle'>('offen');
  const [filterBereich, setFilterBereich] = useState<string>('alle');
  const [filterAgent, setFilterAgent] = useState<string>('alle');

  const offeneCount = useMemo(
    () => vorschlaege.filter(v => !v.entscheidung).length,
    [vorschlaege]
  );

  const displayedVorschlaege = useMemo(() => {
    return vorschlaege
      .filter(v => {
        if (filterStatus === 'offen' && v.entscheidung != null) return false;
        if (filterBereich !== 'alle' && v.bereich !== filterBereich) return false;
        if (filterAgent !== 'alle' && v.meta?.agent_id !== filterAgent) return false;
        return true;
      })
      .sort((a, b) => {
        const aPending = a.entscheidung == null ? 1 : 0;
        const bPending = b.entscheidung == null ? 1 : 0;
        if (aPending !== bPending) return bPending - aPending;
        return new Date(b.erstellt_am).getTime() - new Date(a.erstellt_am).getTime();
      });
  }, [vorschlaege, filterStatus, filterBereich, filterAgent]);

  const handleCreateManual = async () => {
    if (!neuerTitel.trim() || !neuerInhalt.trim()) return;
    setIsSavingManual(true);
    const ok = await onCreateManual({
      titel: neuerTitel.trim(),
      inhalt: neuerInhalt.trim(),
      zielAgent: neuerZielAgent || undefined,
      kategorie: neueKategorie,
      prioritaet: neuePrioritaet,
      typ: neuerTyp,
    });
    setIsSavingManual(false);
    if (ok) {
      toast.success('Vorschlag erstellt und ins System aufgenommen');
      setNeuerTitel('');
      setNeuerInhalt('');
      setNeuerZielAgent('');
      setNeueKategorie('allgemein');
      setNeuePrioritaet(7);
      setNeuerTyp('manuell');
      setShowNeuerVorschlag(false);
    } else {
      toast.error('Fehler beim Erstellen');
    }
  };

  const SCHNELLVORLAGEN = [
    { label: 'Neue Stellungnahme', titel: 'Stellungnahme vorbereiten', inhalt: 'Neue Stellungnahme für Gutachten-Akte vorbereiten.', agent: 'sv-buero', kat: 'sv_buero', typ: 'stellungnahme', prio: 9 },
    { label: 'E-Mail prüfen', titel: 'E-Mail prüfen und zuordnen', inhalt: 'Eingehende E-Mail prüfen und der richtigen Akte zuordnen.', agent: 'email-agent', kat: 'sv_buero', typ: 'email', prio: 7 },
    { label: 'Aufgabe für alle', titel: 'Neue Aufgabe für alle Agenten', inhalt: '', agent: 'tagesmanager', kat: 'allgemein', typ: 'aufgabe', prio: 7 },
    { label: 'Dokument ablegen', titel: 'Dokument in Akte ablegen', inhalt: 'Dokument analysieren und in die richtige Akte ablegen.', agent: 'dokument-agent', kat: 'sv_buero', typ: 'ablage', prio: 6 },
    { label: 'Rechnung prüfen', titel: 'Rechnung prüfen und erfassen', inhalt: 'Eingehende Rechnung prüfen und in der Buchhaltung erfassen.', agent: 'buchhaltung', kat: 'buchhaltung', typ: 'aufgabe', prio: 6 },
  ];

  return (
    <div className="space-y-3">
      {/* ── STICKY FILTER BAR ── */}
      <div
        className="sticky top-0 z-10 flex flex-wrap items-center gap-2 px-3 py-2 rounded-xl border"
        style={{ background: CI.bgPanel, borderColor: CI.border, boxShadow: CI.shadow }}
      >
        {/* Status pills */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => setFilterStatus('offen')}
            className="px-3 py-1 rounded-lg text-xs font-semibold transition-colors"
            style={{
              background: filterStatus === 'offen' ? CI.red : CI.bgSection,
              color: filterStatus === 'offen' ? '#fff' : CI.textSecondary,
            }}
          >
            Offen{offeneCount > 0 ? ` (${offeneCount})` : ''}
          </button>
          <button
            onClick={() => setFilterStatus('alle')}
            className="px-3 py-1 rounded-lg text-xs font-semibold transition-colors"
            style={{
              background: filterStatus === 'alle' ? CI.red : CI.bgSection,
              color: filterStatus === 'alle' ? '#fff' : CI.textSecondary,
            }}
          >
            Alle
          </button>
        </div>

        <div className="w-px h-4 shrink-0" style={{ background: CI.border }} />

        {/* Bereich filter */}
        <select
          value={filterBereich}
          onChange={e => setFilterBereich(e.target.value)}
          className="text-xs rounded-lg px-2 py-1 border focus:outline-none cursor-pointer"
          style={{ borderColor: CI.border, color: CI.textSecondary, background: CI.bgPanel }}
        >
          <option value="alle">Alle Bereiche</option>
          {BEREICHE.map(b => (
            <option key={b.id} value={b.id}>{b.label}</option>
          ))}
        </select>

        {/* Agent filter */}
        <select
          value={filterAgent}
          onChange={e => setFilterAgent(e.target.value)}
          className="text-xs rounded-lg px-2 py-1 border focus:outline-none cursor-pointer"
          style={{ borderColor: CI.border, color: CI.textSecondary, background: CI.bgPanel }}
        >
          <option value="alle">Alle Agenten</option>
          {agenten.map(a => (
            <option key={a.agent_id} value={a.agent_id}>{a.agent_name}</option>
          ))}
        </select>

        {/* Spacer */}
        <div className="flex-1 min-w-0" />

        {/* Neuer Vorschlag button */}
        <button
          onClick={() => setShowNeuerVorschlag(!showNeuerVorschlag)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 border-dashed text-xs font-semibold transition-all active:scale-[0.98] shrink-0"
          style={{
            borderColor: showNeuerVorschlag ? CI.red : CI.border,
            background: showNeuerVorschlag ? CI.redLight : 'transparent',
            color: showNeuerVorschlag ? CI.redDark : CI.textMuted,
          }}
        >
          <Plus className="w-3 h-3" />
          {showNeuerVorschlag ? 'Schließen' : 'Neuer Vorschlag / Anweisung'}
        </button>
      </div>

      {/* ═══ NEUER VORSCHLAG FORMULAR ═══ */}
      {showNeuerVorschlag && (
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: CI.red, boxShadow: '0 4px 16px rgba(196,30,58,0.10)' }}>
          <div className="px-4 py-3 flex items-center gap-2" style={{ backgroundColor: CI.redLight, borderBottom: `1px solid ${CI.redBorder}` }}>
            <Lightbulb className="w-4 h-4" style={{ color: CI.redDark }} />
            <span className="text-xs font-semibold" style={{ color: CI.redDark }}>Neuer Vorschlag ins System</span>
          </div>

          <div className="p-4 space-y-3" style={{ backgroundColor: CI.bgPanel }}>
            {/* Schnellvorlagen */}
            <div className="space-y-1.5">
              <span className="text-[10px] font-medium uppercase tracking-wider" style={{ color: CI.textMuted }}>Schnellvorlagen</span>
              <div className="flex flex-wrap gap-1.5">
                {SCHNELLVORLAGEN.map(vorlage => (
                  <button
                    key={vorlage.label}
                    onClick={() => {
                      setNeuerTitel(vorlage.titel);
                      setNeuerInhalt(vorlage.inhalt);
                      setNeuerZielAgent(vorlage.agent);
                      setNeueKategorie(vorlage.kat);
                      setNeuerTyp(vorlage.typ);
                      setNeuePrioritaet(vorlage.prio);
                    }}
                    className="text-[10px] px-2.5 py-1.5 rounded-lg font-medium transition-all active:scale-95 border"
                    style={{ color: CI.textSecondary, backgroundColor: CI.bgSection, borderColor: CI.border }}
                  >
                    {vorlage.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Titel */}
            <input
              type="text"
              value={neuerTitel}
              onChange={e => setNeuerTitel(e.target.value)}
              placeholder="Titel / Betreff..."
              className="w-full text-xs font-medium border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-red-200"
              style={{ borderColor: CI.border, color: CI.textPrimary }}
            />

            {/* Inhalt */}
            <textarea
              value={neuerInhalt}
              onChange={e => setNeuerInhalt(e.target.value)}
              placeholder="Beschreibung, Anweisung oder Details..."
              rows={3}
              className="w-full text-xs border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-red-200 resize-none"
              style={{ borderColor: CI.border, color: CI.textPrimary }}
            />

            {/* Ziel-Agent */}
            <div className="space-y-1.5">
              <span className="text-[10px] font-medium" style={{ color: CI.textMuted }}>An welchen Agenten?</span>
              <div className="flex flex-wrap gap-1.5">
                <button
                  onClick={() => setNeuerZielAgent('')}
                  className="text-[10px] px-2 py-1.5 rounded-lg transition-all"
                  style={{
                    backgroundColor: !neuerZielAgent ? CI.amberBg : CI.bgSection,
                    color: !neuerZielAgent ? CI.amber : CI.textMuted,
                    border: `1px solid ${!neuerZielAgent ? CI.amber : CI.border}`,
                    fontWeight: !neuerZielAgent ? 600 : 400,
                  }}
                >
                  Alle Agenten
                </button>
                {ROUTING_AGENTEN.slice(0, 10).map(ra => {
                  const RAIcon = ra.icon;
                  const isSelected = neuerZielAgent === ra.id;
                  return (
                    <button
                      key={ra.id}
                      onClick={() => setNeuerZielAgent(ra.id)}
                      className="flex items-center gap-1 text-[10px] px-2 py-1.5 rounded-lg transition-all"
                      style={{
                        backgroundColor: isSelected ? CI.cyanBg : CI.bgSection,
                        color: isSelected ? CI.cyan : CI.textMuted,
                        border: `1px solid ${isSelected ? CI.cyan : CI.border}`,
                        fontWeight: isSelected ? 600 : 400,
                      }}
                    >
                      <RAIcon className="w-3 h-3" />
                      {ra.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Typ & Priorität */}
            <div className="flex items-center gap-3">
              <div className="flex-1 space-y-1">
                <span className="text-[10px] font-medium" style={{ color: CI.textMuted }}>Typ</span>
                <div className="flex flex-wrap gap-1">
                  {[
                    { id: 'manuell', label: 'Allgemein' },
                    { id: 'stellungnahme', label: 'Stellungnahme' },
                    { id: 'aufgabe', label: 'Aufgabe' },
                    { id: 'email', label: 'E-Mail' },
                    { id: 'ablage', label: 'Ablage' },
                  ].map(t => (
                    <button
                      key={t.id}
                      onClick={() => setNeuerTyp(t.id)}
                      className="text-[9px] px-2 py-1 rounded-lg transition-all"
                      style={{
                        backgroundColor: neuerTyp === t.id ? CI.purpleBg : CI.bgSection,
                        color: neuerTyp === t.id ? CI.purple : CI.textMuted,
                        fontWeight: neuerTyp === t.id ? 600 : 400,
                      }}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1">
                <span className="text-[10px] font-medium" style={{ color: CI.textMuted }}>Priorität</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => setNeuePrioritaet(Math.max(1, neuePrioritaet - 1))} className="p-1 rounded" style={{ color: CI.textLight }}>
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                  <span className="text-xs font-bold w-6 text-center" style={{ color: neuePrioritaet >= 8 ? CI.red : neuePrioritaet >= 5 ? CI.amber : CI.textMuted }}>
                    {neuePrioritaet}
                  </span>
                  <button onClick={() => setNeuePrioritaet(Math.min(10, neuePrioritaet + 1))} className="p-1 rounded" style={{ color: CI.textLight }}>
                    <ChevronUp className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Senden */}
            <div className="flex items-center gap-2 pt-1">
              <button
                onClick={handleCreateManual}
                disabled={!neuerTitel.trim() || !neuerInhalt.trim() || isSavingManual}
                className="flex items-center gap-1.5 px-4 py-2.5 text-white text-xs font-semibold rounded-lg disabled:opacity-50 transition-all active:scale-95"
                style={{ backgroundColor: CI.red }}
              >
                {isSavingManual ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Ins System aufnehmen
              </button>
              <button
                onClick={() => { setShowNeuerVorschlag(false); setNeuerTitel(''); setNeuerInhalt(''); }}
                className="text-xs px-3 py-2.5"
                style={{ color: CI.textMuted }}
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Filtered flat list ── */}
      {displayedVorschlaege.length === 0 ? (
        <div
          className="text-center py-8 text-xs rounded-xl border"
          style={{ color: CI.textMuted, borderColor: CI.borderLight, background: CI.bgSection }}
        >
          {filterStatus === 'offen' && filterBereich === 'alle' && filterAgent === 'alle'
            ? 'Keine offenen Vorschläge'
            : 'Keine Vorschläge für diesen Filter'}
        </div>
      ) : (
        displayedVorschlaege.map((v, i) => (
          <VorschlagKarte
            key={v.id}
            vorschlag={v}
            onEntscheiden={onEntscheiden}
            onAktion={onAktion}
            onAufgabeErstellen={onAufgabeErstellen}
            onIssueInRoadmap={onIssueInRoadmap}
            mitarbeiter={mitarbeiter}
            saving={saving}
            index={i}
            agenten={agenten}
          />
        ))
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB: PROTOKOLL
// ═══════════════════════════════════════════════════════════════════════════

function ProtokollTab({
  protokoll,
  protokollProAgent,
  agenten,
}: {
  protokoll: ProtokollEintrag[];
  protokollProAgent: Record<string, ProtokollEintrag[]>;
  agenten: AgentBriefing[];
}) {
  const [ansicht, setAnsicht] = useState<'timeline' | 'agent'>('timeline');
  const [expandedAgent, setExpandedAgent] = useState<string | null>(null);

  if (protokoll.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: CI.bgSection }}>
          <History className="w-7 h-7" style={{ color: CI.textLight }} />
        </div>
        <p className="text-sm font-medium" style={{ color: CI.textMuted }}>Noch kein Protokoll</p>
        <p className="text-xs mt-1" style={{ color: CI.textLight }}>Aktivitäten erscheinen hier sobald Agenten arbeiten</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Ansicht-Umschalter */}
      <div className="flex items-center gap-1 p-1 rounded-lg" style={{ backgroundColor: CI.bgSection }}>
        <button
          onClick={() => setAnsicht('timeline')}
          className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all ${
            ansicht === 'timeline' ? 'bg-white shadow-sm' : ''
          }`}
          style={{ color: ansicht === 'timeline' ? CI.textPrimary : CI.textMuted }}
        >
          <Clock className="w-3.5 h-3.5" />
          Zeitverlauf
        </button>
        <button
          onClick={() => setAnsicht('agent')}
          className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all ${
            ansicht === 'agent' ? 'bg-white shadow-sm' : ''
          }`}
          style={{ color: ansicht === 'agent' ? CI.textPrimary : CI.textMuted }}
        >
          <ListChecks className="w-3.5 h-3.5" />
          Pro Agent
        </button>
      </div>

      {/* Zusammenfassung */}
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-xl border p-3 text-center" style={{ borderColor: CI.border, backgroundColor: CI.bgPanel, boxShadow: CI.shadow }}>
          <p className="text-lg font-bold" style={{ color: CI.textPrimary }}>{protokoll.length}</p>
          <p className="text-[10px] font-medium" style={{ color: CI.textMuted }}>Aktionen gesamt</p>
        </div>
        <div className="rounded-xl border p-3 text-center" style={{ borderColor: '#A7F3D0', backgroundColor: CI.emeraldBg }}>
          <p className="text-lg font-bold" style={{ color: CI.emerald }}>{protokoll.filter(p => p.erfolgreich).length}</p>
          <p className="text-[10px] font-medium" style={{ color: CI.emerald }}>Erfolgreich</p>
        </div>
        <div className="rounded-xl border p-3 text-center" style={{ borderColor: CI.border, backgroundColor: CI.bgPanel, boxShadow: CI.shadow }}>
          <p className="text-lg font-bold" style={{ color: CI.textPrimary }}>{Object.keys(protokollProAgent).length}</p>
          <p className="text-[10px] font-medium" style={{ color: CI.textMuted }}>Agenten aktiv</p>
        </div>
      </div>

      {/* Timeline-Ansicht */}
      {ansicht === 'timeline' && (
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: CI.border, boxShadow: CI.shadow }}>
          {protokoll.map((eintrag, idx) => {
            const aktionConf = AKTION_CONFIG[eintrag.aktion] || {
              icon: Activity, label: eintrag.aktion.replace(/_/g, ' '),
              color: CI.textMuted, bg: CI.bgSection,
            };
            const AktionIcon = aktionConf.icon;
            const agent = agenten.find(a => a.agent_id === eintrag.agent_id);
            const AgentIcon = agent ? getIcon(agent.icon) : CircleDot;
            const zebraBg = idx % 2 === 0 ? CI.bgZebraEven : CI.bgZebraOdd;

            return (
              <div
                key={eintrag.id}
                className="flex gap-3 px-4 py-3 transition-colors"
                style={{
                  backgroundColor: zebraBg,
                  borderBottom: idx < protokoll.length - 1 ? `1px solid ${CI.borderLight}` : 'none',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = CI.bgHover; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = zebraBg; }}
              >
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: aktionConf.bg }}
                >
                  <AktionIcon className="w-4 h-4" style={{ color: aktionConf.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold" style={{ color: CI.textPrimary }}>{aktionConf.label}</span>
                    <span className="text-[10px] flex items-center gap-1" style={{ color: CI.textMuted }}>
                      <AgentIcon className="w-3 h-3" style={{ color: agent?.farbe || CI.textMuted }} />
                      {agent?.agent_name || eintrag.agent_name}
                    </span>
                  </div>
                  <p className="text-xs mt-1 leading-relaxed" style={{ color: CI.textSecondary }}>{eintrag.beschreibung}</p>

                  {/* Details-Chips */}
                  {eintrag.details && Object.keys(eintrag.details).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {Object.entries(eintrag.details).slice(0, 4).map(([key, val]) => (
                        <span key={key} className="text-[9px] font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: CI.bgSection, color: CI.textSecondary }}>
                          {key}: {typeof val === 'object' ? JSON.stringify(val) : String(val)}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-[10px]" style={{ color: CI.textLight }}>{zeitAgo(eintrag.erstellt_am)}</span>
                    {eintrag.erfolgreich ? (
                      <span className="text-[9px] font-medium flex items-center gap-0.5" style={{ color: CI.emerald }}>
                        <CheckCircle2 className="w-3 h-3" /> Erfolgreich
                      </span>
                    ) : (
                      <span className="text-[9px] font-medium flex items-center gap-0.5" style={{ color: CI.red }}>
                        <XCircle className="w-3 h-3" /> Fehler
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pro-Agent-Ansicht */}
      {ansicht === 'agent' && (
        <div className="space-y-2">
          {agenten.filter(a => protokollProAgent[a.agent_id]?.length).map((agent, agentIdx) => {
            const AgentIcon = getIcon(agent.icon);
            const eintraege = protokollProAgent[agent.agent_id] || [];
            const isExpanded = expandedAgent === agent.agent_id;

            return (
              <div
                key={agent.agent_id}
                className="rounded-xl border overflow-hidden"
                style={{ borderColor: CI.border, boxShadow: CI.shadow }}
              >
                <button
                  onClick={() => setExpandedAgent(isExpanded ? null : agent.agent_id)}
                  className="w-full flex items-center gap-3 px-4 py-3 transition-colors"
                  style={{ backgroundColor: agentIdx % 2 === 0 ? CI.bgZebraEven : CI.bgZebraOdd }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = CI.bgHover; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = agentIdx % 2 === 0 ? CI.bgZebraEven : CI.bgZebraOdd; }}
                >
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${agent.farbe}12` }}
                  >
                    <AgentIcon className="w-4.5 h-4.5" style={{ color: agent.farbe }} />
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-xs font-semibold" style={{ color: CI.textPrimary }}>{agent.agent_name}</p>
                    <p className="text-[10px]" style={{ color: CI.textMuted }}>{eintraege.length} Aktionen</p>
                  </div>
                  <span className="text-xs font-bold mr-1" style={{ color: CI.textLight }}>{eintraege.length}</span>
                  {isExpanded ? <ChevronUp className="w-4 h-4" style={{ color: CI.textMuted }} /> : <ChevronDown className="w-4 h-4" style={{ color: CI.textMuted }} />}
                </button>

                {isExpanded && (
                  <div style={{ borderTop: `1px solid ${CI.borderLight}` }}>
                    {eintraege.map((eintrag, eIdx) => {
                      const aktionConf = AKTION_CONFIG[eintrag.aktion] || {
                        icon: Activity, label: eintrag.aktion.replace(/_/g, ' '),
                        color: CI.textMuted, bg: CI.bgSection,
                      };
                      const AktionIcon = aktionConf.icon;
                      const zebraBg = eIdx % 2 === 0 ? CI.bgZebraEven : CI.bgZebraOdd;
                      return (
                        <div
                          key={eintrag.id}
                          className="flex items-start gap-3 px-4 py-3"
                          style={{
                            backgroundColor: zebraBg,
                            borderBottom: eIdx < eintraege.length - 1 ? `1px solid ${CI.borderLight}` : 'none',
                          }}
                        >
                          <div
                            className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5"
                            style={{ backgroundColor: aktionConf.bg }}
                          >
                            <AktionIcon className="w-3.5 h-3.5" style={{ color: aktionConf.color }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs" style={{ color: CI.textSecondary }}>{eintrag.beschreibung}</p>
                            <div className="flex items-center gap-2 mt-1.5">
                              <span className="text-[10px]" style={{ color: CI.textLight }}>{zeitAgo(eintrag.erstellt_am)}</span>
                              {eintrag.erfolgreich && <CheckCircle2 className="w-3 h-3" style={{ color: CI.emerald }} />}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB: AGENTEN
// ═══════════════════════════════════════════════════════════════════════════

function AgentenTab({ agenten }: { agenten: AgentBriefing[] }) {
  const [expandedAgent, setExpandedAgent] = useState<string | null>(null);

  return (
    <div className="space-y-2">
      {agenten.map((agent, idx) => {
        const isExpanded = expandedAgent === agent.agent_id;
        const AgentIcon = getIcon(agent.icon);
        const zebraBg = idx % 2 === 0 ? CI.bgZebraEven : CI.bgZebraOdd;

        return (
          <div
            key={agent.agent_id}
            className={`rounded-xl border transition-all overflow-hidden ${
              agent.ist_aktiv ? '' : 'opacity-60'
            }`}
            style={{
              borderColor: isExpanded ? CI.redBorder : CI.border,
              boxShadow: isExpanded ? CI.shadowHover : CI.shadow,
            }}
          >
            <button
              onClick={() => setExpandedAgent(isExpanded ? null : agent.agent_id)}
              className="w-full flex items-center gap-3 px-4 py-3.5 transition-colors"
              style={{ backgroundColor: zebraBg }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = CI.bgHover; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = zebraBg; }}
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${agent.farbe}15` }}
              >
                <AgentIcon className="w-5 h-5" style={{ color: agent.farbe }} />
              </div>
              <div className="flex-1 text-left min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold" style={{ color: CI.textPrimary }}>{agent.agent_name}</p>
                  {agent.ist_aktiv ? (
                    agent.managed_agent_id ? (
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: CI.emerald }} title="Aktiv auf platform.claude.ai" />
                    ) : (
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: CI.amber }} title="Aktiv in DB, kein Platform-Link" />
                    )
                  ) : (
                    <span className="text-[9px] font-medium px-1.5 py-0.5 rounded" style={{ color: CI.textMuted, backgroundColor: CI.bgSection }}>Inaktiv</span>
                  )}
                </div>
                <p className="text-[11px] mt-0.5 truncate" style={{ color: CI.textMuted }}>{agent.beschreibung}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-[10px] hidden sm:block" style={{ color: CI.textMuted }}>{agent.intervall}</span>
                {isExpanded ? <ChevronUp className="w-4 h-4" style={{ color: CI.textMuted }} /> : <ChevronDown className="w-4 h-4" style={{ color: CI.textMuted }} />}
              </div>
            </button>

            {isExpanded && (
              <div className="px-4 pb-4 pt-3 space-y-3" style={{ borderTop: `1px solid ${CI.borderLight}`, backgroundColor: CI.bgSection }}>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg px-3 py-2" style={{ backgroundColor: CI.bgPanel, border: `1px solid ${CI.borderLight}` }}>
                    <p className="text-[10px] font-medium" style={{ color: CI.textMuted }}>Intervall</p>
                    <p className="text-xs font-semibold mt-0.5" style={{ color: CI.textSecondary }}>{agent.intervall}</p>
                  </div>
                  <div className="rounded-lg px-3 py-2" style={{ backgroundColor: CI.bgPanel, border: `1px solid ${CI.borderLight}` }}>
                    <p className="text-[10px] font-medium" style={{ color: CI.textMuted }}>Status</p>
                    <p className="text-xs font-semibold mt-0.5" style={{ color: agent.ist_aktiv ? CI.emerald : CI.textMuted }}>
                      {agent.ist_aktiv ? 'Aktiv' : 'Deaktiviert'}
                    </p>
                    {agent.ist_aktiv && !agent.managed_agent_id && (
                      <span className="text-[9px] font-medium px-1.5 py-0.5 rounded mt-1 inline-block" style={{ backgroundColor: CI.amberBg, color: CI.amber }}>
                        Kein Platform-Agent
                      </span>
                    )}
                  </div>
                </div>

                {/* Bereiche */}
                {agent.bereiche && agent.bereiche.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {agent.bereiche.map((b: string) => {
                      const bereich = BEREICHE.find(br => br.id === b);
                      return (
                        <span key={b} className="text-[9px] font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: CI.bgSection, color: CI.textSecondary, border: `1px solid ${CI.borderLight}` }}>
                          {bereich?.label || b}
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Berechtigungen */}
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: 'darf_emails_senden', label: 'E-Mails senden', icon: Mail },
                    { key: 'darf_dokumente_ablegen', label: 'Dokumente ablegen', icon: FolderInput },
                    { key: 'darf_aufgaben_erstellen', label: 'Aufgaben erstellen', icon: Plus },
                    { key: 'darf_zahlungen_vorschlagen', label: 'Zahlungen vorschlagen', icon: Wallet },
                  ].map(perm => {
                    const erlaubt = (agent as any)[perm.key];
                    const PermIcon = perm.icon;
                    return (
                      <div
                        key={perm.key}
                        className="flex items-center gap-1.5 text-[10px] rounded-lg px-2.5 py-1.5"
                        style={{
                          backgroundColor: erlaubt ? CI.emeraldBg : CI.bgSection,
                          color: erlaubt ? CI.emerald : CI.textMuted,
                          border: `1px solid ${erlaubt ? '#A7F3D0' : CI.borderLight}`,
                        }}
                      >
                        <PermIcon className="w-3 h-3" />
                        {perm.label}
                      </div>
                    );
                  })}
                </div>

                {/* Beschreibung */}
                <div className="rounded-lg px-3.5 py-3" style={{ backgroundColor: CI.bgPanel, border: `1px solid ${CI.borderLight}` }}>
                  <p className="text-[10px] font-medium uppercase tracking-wide mb-1.5" style={{ color: CI.textMuted }}>Beschreibung</p>
                  <p className="text-xs leading-relaxed" style={{ color: CI.textSecondary }}>{agent.beschreibung}</p>
                </div>

                {/* Briefing */}
                {agent.briefing_inhalt && (
                  <div className="rounded-lg px-3.5 py-3" style={{ backgroundColor: CI.blueBg, border: `1px solid #BFDBFE` }}>
                    <p className="text-[10px] font-medium uppercase tracking-wide mb-1.5" style={{ color: CI.blue }}>Briefing</p>
                    <p className="text-[11px] leading-relaxed whitespace-pre-wrap line-clamp-6" style={{ color: '#1E40AF' }}>
                      {agent.briefing_inhalt.replace(/^#.*\n/gm, '').trim().substring(0, 500)}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB: LERNREGELN
// ═══════════════════════════════════════════════════════════════════════════

function LernregelnTab({
  regeln,
  onToggle,
  onCreate,
}: {
  regeln: LernRegel[];
  onToggle: (id: string, aktiv: boolean) => Promise<boolean>;
  onCreate: (regel: { kategorie: string; muster: string; aktion: string; beispiel?: string }) => Promise<boolean>;
}) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ kategorie: '', muster: '', aktion: '', beispiel: '' });

  const handleCreate = async () => {
    if (!form.muster.trim() || !form.aktion.trim()) {
      toast.error('Muster und Aktion sind Pflichtfelder');
      return;
    }
    const ok = await onCreate(form);
    if (ok) {
      toast.success('Lernregel erstellt');
      setShowForm(false);
      setForm({ kategorie: '', muster: '', aktion: '', beispiel: '' });
    }
  };

  // Gruppiere nach Kategorie
  const grouped: Record<string, LernRegel[]> = {};
  for (const r of regeln) {
    const cat = r.kategorie || 'Allgemein';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(r);
  }

  return (
    <div className="space-y-4">
      {/* Info-Banner */}
      <div className="rounded-xl p-4" style={{ background: `linear-gradient(135deg, ${CI.purpleBg}, ${CI.blueBg})`, border: `1px solid #C4B5FD` }}>
        <div className="flex items-start gap-3">
          <Brain className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: CI.purple }} />
          <div>
            <p className="text-xs font-semibold" style={{ color: '#312E81' }}>Lernfähiges System</p>
            <p className="text-[11px] mt-1 leading-relaxed" style={{ color: '#4338CA' }}>
              Der Agent merkt sich deine Entscheidungen. Wenn du einen Vorschlag 3x genehmigst, wird er automatisch als Regel vorgeschlagen.
            </p>
          </div>
        </div>
      </div>

      {/* Neue Regel Button */}
      <button
        onClick={() => setShowForm(!showForm)}
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-dashed text-xs font-medium transition-all"
        style={{ borderColor: CI.textLight, color: CI.textMuted }}
        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = CI.textMuted; (e.currentTarget as HTMLElement).style.backgroundColor = CI.bgSection; }}
        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = CI.textLight; (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
      >
        <Plus className="w-4 h-4" />
        Neue Regel erstellen
      </button>

      {/* Formular */}
      {showForm && (
        <div className="rounded-xl p-4 space-y-3" style={{ backgroundColor: CI.blueBg, border: `1px solid #BFDBFE` }}>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-medium uppercase tracking-wide" style={{ color: CI.textMuted }}>Kategorie</label>
              <input
                value={form.kategorie}
                onChange={e => setForm(p => ({ ...p, kategorie: e.target.value }))}
                placeholder="z.B. E-Mail"
                className="w-full mt-1 px-3 py-2 rounded-lg border text-xs focus:outline-none focus:ring-2 focus:ring-blue-200"
                style={{ borderColor: CI.border, color: CI.textPrimary }}
              />
            </div>
            <div>
              <label className="text-[10px] font-medium uppercase tracking-wide" style={{ color: CI.textMuted }}>Beispiel</label>
              <input
                value={form.beispiel}
                onChange={e => setForm(p => ({ ...p, beispiel: e.target.value }))}
                placeholder="Optional"
                className="w-full mt-1 px-3 py-2 rounded-lg border text-xs focus:outline-none focus:ring-2 focus:ring-blue-200"
                style={{ borderColor: CI.border, color: CI.textPrimary }}
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-medium uppercase tracking-wide" style={{ color: CI.textMuted }}>Wenn dieses Muster erkannt wird...</label>
            <input
              value={form.muster}
              onChange={e => setForm(p => ({ ...p, muster: e.target.value }))}
              placeholder="z.B. Newsletter von unbekanntem Absender"
              className="w-full mt-1 px-3 py-2 rounded-lg border text-xs focus:outline-none focus:ring-2 focus:ring-blue-200"
              style={{ borderColor: CI.border, color: CI.textPrimary }}
            />
          </div>
          <div>
            <label className="text-[10px] font-medium uppercase tracking-wide" style={{ color: CI.textMuted }}>...dann diese Aktion ausführen</label>
            <input
              value={form.aktion}
              onChange={e => setForm(p => ({ ...p, aktion: e.target.value }))}
              placeholder="z.B. Automatisch archivieren"
              className="w-full mt-1 px-3 py-2 rounded-lg border text-xs focus:outline-none focus:ring-2 focus:ring-blue-200"
              style={{ borderColor: CI.border, color: CI.textPrimary }}
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleCreate}
              className="flex items-center gap-1.5 px-4 py-2 text-white text-xs font-medium rounded-lg transition-all"
              style={{ backgroundColor: CI.blue }}
            >
              <Plus className="w-3.5 h-3.5" />
              Erstellen
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="text-xs px-3 py-2"
              style={{ color: CI.textMuted }}
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {/* Regeln nach Kategorie */}
      {Object.entries(grouped).map(([kategorie, rules]) => (
        <div key={kategorie}>
          <p className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: CI.textMuted }}>{kategorie}</p>
          <div className="rounded-xl border overflow-hidden" style={{ borderColor: CI.border, boxShadow: CI.shadow }}>
            {rules.map((regel, rIdx) => (
              <div
                key={regel.id}
                className={`p-3.5 transition-all ${!regel.aktiv ? 'opacity-60' : ''}`}
                style={{
                  backgroundColor: rIdx % 2 === 0 ? CI.bgZebraEven : CI.bgZebraOdd,
                  borderBottom: rIdx < rules.length - 1 ? `1px solid ${CI.borderLight}` : 'none',
                }}
              >
                <div className="flex items-start gap-3">
                  <button
                    onClick={() => onToggle(regel.id, !regel.aktiv)}
                    className="flex-shrink-0 mt-0.5"
                    title={regel.aktiv ? 'Deaktivieren' : 'Aktivieren'}
                  >
                    {regel.aktiv ? (
                      <ToggleRight className="w-5 h-5" style={{ color: CI.emerald }} />
                    ) : (
                      <ToggleLeft className="w-5 h-5" style={{ color: CI.textMuted }} />
                    )}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold" style={{ color: CI.textPrimary }}>{regel.muster}</p>
                    <p className="text-[11px] mt-1" style={{ color: CI.textSecondary }}>{regel.aktion}</p>
                    {regel.beispiel && (
                      <p className="text-[10px] mt-1.5 italic" style={{ color: CI.textMuted }}>Beispiel: {regel.beispiel}</p>
                    )}
                  </div>
                  {regel.anwendungen > 0 && (
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: CI.bgSection, color: CI.textMuted }}>
                      {regel.anwendungen}x
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB: ROADMAP
// ═══════════════════════════════════════════════════════════════════════════

const STATUS_CONFIG: Record<string, { label: string; farbe: string; bg: string; icon: React.ElementType }> = {
  idee:      { label: 'Idee',      farbe: CI.textSecondary, bg: CI.bgSection,  icon: Lightbulb },
  geplant:   { label: 'Geplant',   farbe: CI.blue,          bg: CI.blueBg,     icon: Target },
  in_arbeit: { label: 'In Arbeit', farbe: CI.amber,         bg: CI.amberBg,    icon: Rocket },
  review:    { label: 'Review',    farbe: CI.purple,        bg: CI.purpleBg,   icon: Eye },
  fertig:    { label: 'Fertig',    farbe: CI.emerald,       bg: CI.emeraldBg,  icon: CheckCircle2 },
};

const KATEGORIE_CONFIG: Record<string, { label: string; icon: React.ElementType; farbe: string }> = {
  app:          { label: 'App',         icon: Rocket,     farbe: CI.blue },
  agenten:      { label: 'Agenten',     icon: Brain,      farbe: CI.purple },
  buchhaltung:  { label: 'Buchhaltung', icon: Wallet,     farbe: CI.emerald },
  marketing:    { label: 'Marketing',   icon: TrendingUp, farbe: '#DB2777' },
  technik:      { label: 'Technik',     icon: Wrench,     farbe: CI.textSecondary },
  immobilien:   { label: 'Immobilien',  icon: Building,   farbe: CI.amber },
  integration:  { label: 'API/MCP',     icon: Plug,       farbe: CI.cyan },
  allgemein:    { label: 'Allgemein',   icon: CircleDot,  farbe: CI.textMuted },
};

const TYP_ICONS: Record<string, React.ElementType> = {
  feature: Rocket,
  bugfix: Bug,
  verbesserung: Wrench,
  idee: Lightbulb,
  todo: ListChecks,
  api_todo: KeyRound,
  mcp_todo: Plug,
};

function RoadmapTab() {
  const { bereiche, getEintraegeByBereich, createEintrag, updateEintrag, deleteEintrag, refetch, loading: cockpitLoading } = useKiCockpit();
  const [filter, setFilter] = useState<string>('alle');
  const [showNeuesItem, setShowNeuesItem] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [feedbackText, setFeedbackText] = useState<Record<string, string>>({});
  const [neuesItem, setNeuesItem] = useState({ titel: '', inhalt: '', typ: 'feature', kategorie: 'app', prioritaet: 5 });
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{ added: number; skipped: number; errors: number } | null>(null);

  const roadmapItems = getEintraegeByBereich('roadmap');

  // Sync-Funktion
  const handleSync = async () => {
    const roadmapBereich = bereiche.find(b => b.bereich === 'roadmap');
    if (!roadmapBereich) { toast.error('Roadmap-Bereich nicht gefunden'); return; }
    setSyncing(true);
    setSyncResult(null);
    let added = 0, skipped = 0, errors = 0;
    const existingSyncIds = new Set(roadmapItems.filter(item => item.meta?.sync_id).map(item => item.meta!.sync_id as string));
    for (const syncItem of ROADMAP_SYNC_ITEMS) {
      if (existingSyncIds.has(syncItem.sync_id)) { skipped++; continue; }
      try {
        const success = await createEintrag({
          bereich_id: roadmapBereich.id,
          typ: syncItem.typ,
          titel: syncItem.titel,
          inhalt: syncItem.inhalt,
          prioritaet: syncItem.prioritaet,
          meta: { sync_id: syncItem.sync_id, status: syncItem.status, kategorie: syncItem.kategorie, quelle: syncItem.quelle, abhaengigkeit: syncItem.abhaengigkeit || null, anleitung: syncItem.anleitung || null, prioritaet: syncItem.prioritaet, feedback: [], synced_at: new Date().toISOString() },
          erstellt_von: 'system',
        });
        if (success) added++; else errors++;
      } catch { errors++; }
    }
    setSyncResult({ added, skipped, errors });
    setSyncing(false);
    if (added > 0) toast.success(`${added} neue Einträge synchronisiert`);
    else if (skipped > 0) toast.info(`Roadmap ist synchron (${skipped} vorhanden)`);
    if (errors > 0) toast.error(`${errors} Fehler`);
  };

  const filteredItems = filter === 'alle' ? roadmapItems : roadmapItems.filter(item => {
    const meta = item.meta || {};
    return meta.status === filter || meta.kategorie === filter;
  });

  const sortedItems = [...filteredItems].sort((a, b) => {
    const prioA = a.meta?.prioritaet || a.prioritaet || 0;
    const prioB = b.meta?.prioritaet || b.prioritaet || 0;
    if (prioB !== prioA) return prioB - prioA;
    return new Date(b.erstellt_am).getTime() - new Date(a.erstellt_am).getTime();
  });

  const stats = {
    gesamt: roadmapItems.length,
    ideen: roadmapItems.filter(i => (i.meta?.status || 'idee') === 'idee').length,
    geplant: roadmapItems.filter(i => i.meta?.status === 'geplant').length,
    inArbeit: roadmapItems.filter(i => i.meta?.status === 'in_arbeit').length,
    review: roadmapItems.filter(i => i.meta?.status === 'review').length,
    fertig: roadmapItems.filter(i => i.meta?.status === 'fertig').length,
  };

  const handleCreate = async () => {
    if (!neuesItem.titel.trim()) { toast.error('Titel erforderlich'); return; }
    const roadmapBereich = bereiche.find(b => b.bereich === 'roadmap');
    if (!roadmapBereich) { toast.error('Roadmap-Bereich nicht gefunden'); return; }
    await createEintrag({
      bereich_id: roadmapBereich.id, typ: neuesItem.typ, titel: neuesItem.titel, inhalt: neuesItem.inhalt, prioritaet: neuesItem.prioritaet,
      meta: { status: 'idee', kategorie: neuesItem.kategorie, feedback: [], prioritaet: neuesItem.prioritaet },
      erstellt_von: 'admin',
    });
    setNeuesItem({ titel: '', inhalt: '', typ: 'feature', kategorie: 'app', prioritaet: 5 });
    setShowNeuesItem(false);
  };

  const handleStatusChange = async (item: KiCockpitEintrag, neuerStatus: string) => {
    await updateEintrag(item.id, { meta: { ...item.meta, status: neuerStatus, letzteAenderung: new Date().toISOString() } });
  };

  const handleFeedback = async (item: KiCockpitEintrag, entscheidung: 'genehmigt' | 'abgelehnt' | 'angepasst') => {
    const text = feedbackText[item.id] || '';
    const bisherigeFeedbacks = item.meta?.feedback || [];
    await updateEintrag(item.id, {
      meta: {
        ...item.meta,
        feedback: [...bisherigeFeedbacks, { entscheidung, text, datum: new Date().toISOString(), von: 'admin' }],
        status: entscheidung === 'genehmigt' ? (item.meta?.status === 'review' ? 'fertig' : 'geplant') : entscheidung === 'abgelehnt' ? 'idee' : item.meta?.status,
        letzteAenderung: new Date().toISOString(),
      },
    });
    setFeedbackText(prev => ({ ...prev, [item.id]: '' }));
    setExpandedId(null);
    toast.success(entscheidung === 'genehmigt' ? 'Genehmigt' : entscheidung === 'abgelehnt' ? 'Abgelehnt' : 'Feedback gespeichert');
  };

  const handlePrioChange = async (item: KiCockpitEintrag, delta: number) => {
    const neuePrio = Math.max(1, Math.min(10, (item.meta?.prioritaet || item.prioritaet || 5) + delta));
    await updateEintrag(item.id, { prioritaet: neuePrio, meta: { ...item.meta, prioritaet: neuePrio } });
  };

  return (
    <div className="space-y-4">
      {/* Stats-Leiste */}
      <div className="grid grid-cols-5 gap-1.5">
        {(['idee', 'geplant', 'in_arbeit', 'review', 'fertig'] as const).map(status => {
          const cfg = STATUS_CONFIG[status];
          const count = status === 'idee' ? stats.ideen : status === 'geplant' ? stats.geplant : status === 'in_arbeit' ? stats.inArbeit : status === 'review' ? stats.review : stats.fertig;
          const StatusIcon = cfg.icon;
          const isActive = filter === status;
          return (
            <button
              key={status}
              onClick={() => setFilter(isActive ? 'alle' : status)}
              className="rounded-lg p-2 text-center transition-all border"
              style={{
                backgroundColor: isActive ? cfg.bg : CI.bgPanel,
                borderColor: isActive ? cfg.farbe : CI.borderLight,
                boxShadow: isActive ? CI.shadow : undefined,
              }}
            >
              <StatusIcon className="w-3.5 h-3.5 mx-auto mb-0.5" style={{ color: isActive ? cfg.farbe : CI.textMuted }} />
              <div className="text-lg font-bold" style={{ color: isActive ? cfg.farbe : CI.textSecondary }}>{count}</div>
              <div className="text-[9px] leading-tight" style={{ color: CI.textMuted }}>{cfg.label}</div>
            </button>
          );
        })}
      </div>

      {/* Filter + Neues Item */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 overflow-x-auto">
          <button
            onClick={() => setFilter('alle')}
            className="text-[10px] px-2.5 py-1 rounded-full whitespace-nowrap transition-colors"
            style={{
              backgroundColor: filter === 'alle' ? CI.redDark : CI.bgSection,
              color: filter === 'alle' ? '#FFFFFF' : CI.textMuted,
            }}
          >
            Alle ({stats.gesamt})
          </button>
          {Object.entries(KATEGORIE_CONFIG).map(([key, cfg]) => {
            const count = roadmapItems.filter(i => (i.meta?.kategorie || 'allgemein') === key).length;
            if (count === 0) return null;
            const KatIcon = cfg.icon;
            const isActive = filter === key;
            return (
              <button
                key={key}
                onClick={() => setFilter(isActive ? 'alle' : key)}
                className="text-[10px] px-2.5 py-1 rounded-full whitespace-nowrap flex items-center gap-1 transition-colors"
                style={{
                  backgroundColor: isActive ? CI.redDark : CI.bgSection,
                  color: isActive ? '#FFFFFF' : CI.textMuted,
                }}
              >
                <KatIcon className="w-3 h-3" />
                {cfg.label} ({count})
              </button>
            );
          })}
        </div>
        <button
          onClick={() => setShowNeuesItem(!showNeuesItem)}
          className="flex items-center gap-1 text-[10px] font-medium px-3 py-1.5 rounded-full transition-colors flex-shrink-0"
          style={{ color: CI.emerald, backgroundColor: CI.emeraldBg }}
        >
          <Plus className="w-3 h-3" />
          Idee
        </button>
      </div>

      {/* Neues Item Formular */}
      {showNeuesItem && (
        <div className="rounded-xl p-4 space-y-3" style={{ backgroundColor: CI.emeraldBg, border: `1px solid #A7F3D0` }}>
          <input
            type="text"
            placeholder="Feature / Idee / Verbesserung..."
            value={neuesItem.titel}
            onChange={e => setNeuesItem(prev => ({ ...prev, titel: e.target.value }))}
            className="w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-200"
            style={{ borderColor: CI.border, color: CI.textPrimary }}
          />
          <textarea
            placeholder="Beschreibung (optional)..."
            value={neuesItem.inhalt}
            onChange={e => setNeuesItem(prev => ({ ...prev, inhalt: e.target.value }))}
            rows={2}
            className="w-full text-xs border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-200 resize-none"
            style={{ borderColor: CI.border, color: CI.textPrimary }}
          />
          <div className="flex items-center gap-2 flex-wrap">
            <select value={neuesItem.typ} onChange={e => setNeuesItem(prev => ({ ...prev, typ: e.target.value }))} className="text-[10px] border rounded-lg px-2 py-1.5 bg-white" style={{ borderColor: CI.border }}>
              <option value="feature">Feature</option>
              <option value="bugfix">Bugfix</option>
              <option value="verbesserung">Verbesserung</option>
              <option value="idee">Idee</option>
              <option value="todo">TODO</option>
              <option value="api_todo">API-TODO</option>
              <option value="mcp_todo">MCP-TODO</option>
            </select>
            <select value={neuesItem.kategorie} onChange={e => setNeuesItem(prev => ({ ...prev, kategorie: e.target.value }))} className="text-[10px] border rounded-lg px-2 py-1.5 bg-white" style={{ borderColor: CI.border }}>
              {Object.entries(KATEGORIE_CONFIG).map(([key, cfg]) => (
                <option key={key} value={key}>{cfg.label}</option>
              ))}
            </select>
            <div className="flex items-center gap-1">
              <span className="text-[10px]" style={{ color: CI.textMuted }}>Prio:</span>
              <input type="range" min={1} max={10} value={neuesItem.prioritaet} onChange={e => setNeuesItem(prev => ({ ...prev, prioritaet: parseInt(e.target.value) }))} className="w-16 h-1" style={{ accentColor: CI.emerald }} />
              <span className="text-[10px] font-mono w-4" style={{ color: CI.textSecondary }}>{neuesItem.prioritaet}</span>
            </div>
            <div className="flex-1" />
            <button onClick={() => setShowNeuesItem(false)} className="text-[10px] px-2 py-1" style={{ color: CI.textMuted }}>Abbrechen</button>
            <button onClick={handleCreate} className="text-[10px] font-medium text-white px-3 py-1.5 rounded-lg transition-colors" style={{ backgroundColor: CI.emerald }}>Erstellen</button>
          </div>
        </div>
      )}

      {/* Roadmap-Items */}
      {cockpitLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 animate-spin" style={{ color: CI.textMuted }} />
        </div>
      ) : sortedItems.length === 0 ? (
        <div className="text-center py-12">
          <Map className="w-10 h-10 mx-auto mb-3" style={{ color: CI.textLight }} />
          <p className="text-sm" style={{ color: CI.textMuted }}>Keine Roadmap-Einträge</p>
          <p className="text-[10px] mt-1" style={{ color: CI.textLight }}>Erstelle eine neue Idee oder synchronisiere die Roadmap</p>
        </div>
      ) : (
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: CI.border, boxShadow: CI.shadow }}>
          {sortedItems.map((item, itemIdx) => {
            const meta = item.meta || {};
            const status = meta.status || 'idee';
            const kategorie = meta.kategorie || 'allgemein';
            const prio = meta.prioritaet || item.prioritaet || 5;
            const feedbacks = meta.feedback || [];
            const statusCfg = STATUS_CONFIG[status] || STATUS_CONFIG.idee;
            const katCfg = KATEGORIE_CONFIG[kategorie] || KATEGORIE_CONFIG.allgemein;
            const TypIcon = TYP_ICONS[item.typ] || CircleDot;
            const StatusIcon = statusCfg.icon;
            const KatIcon = katCfg.icon;
            const isExpanded = expandedId === item.id;
            const isKi = item.erstellt_von === 'ki_agent';
            const zebraBg = itemIdx % 2 === 0 ? CI.bgZebraEven : CI.bgZebraOdd;

            return (
              <div
                key={item.id}
                style={{
                  backgroundColor: isExpanded ? CI.bgSection : zebraBg,
                  borderBottom: itemIdx < sortedItems.length - 1 ? `1px solid ${CI.borderLight}` : 'none',
                }}
              >
                {/* Header */}
                <button
                  onClick={() => setExpandedId(isExpanded ? null : item.id)}
                  className="w-full flex items-start gap-3 p-3.5 text-left transition-colors"
                  onMouseEnter={e => { if (!isExpanded) (e.currentTarget as HTMLElement).style.backgroundColor = CI.bgHover; }}
                  onMouseLeave={e => { if (!isExpanded) (e.currentTarget as HTMLElement).style.backgroundColor = zebraBg; }}
                >
                  {/* Priorität */}
                  <div className="flex flex-col items-center gap-0.5 flex-shrink-0 pt-0.5">
                    <button
                      onClick={e => { e.stopPropagation(); handlePrioChange(item, 1); }}
                      className="p-0.5 rounded"
                      style={{ color: CI.textLight }}
                    >
                      <ChevronUp className="w-3 h-3" />
                    </button>
                    <span className="text-xs font-bold w-5 text-center" style={{ color: prio >= 8 ? CI.red : prio >= 5 ? CI.amber : CI.textMuted }}>{prio}</span>
                    <button
                      onClick={e => { e.stopPropagation(); handlePrioChange(item, -1); }}
                      className="p-0.5 rounded"
                      style={{ color: CI.textLight }}
                    >
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap mb-1">
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium flex items-center gap-0.5" style={{ backgroundColor: statusCfg.bg, color: statusCfg.farbe }}>
                        <StatusIcon className="w-2.5 h-2.5" /> {statusCfg.label}
                      </span>
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium flex items-center gap-0.5" style={{ backgroundColor: CI.bgSection, color: CI.textMuted }}>
                        <KatIcon className="w-2.5 h-2.5" /> {katCfg.label}
                      </span>
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5" style={{ backgroundColor: CI.bgSection, color: CI.textMuted }}>
                        <TypIcon className="w-2.5 h-2.5" /> {item.typ}
                      </span>
                      {isKi && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium flex items-center gap-0.5" style={{ backgroundColor: CI.purpleBg, color: CI.purple }}>
                          <Brain className="w-2.5 h-2.5" /> Agent
                        </span>
                      )}
                      {feedbacks.length > 0 && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: CI.blueBg, color: CI.blue }}>
                          {feedbacks.length} Feedback{feedbacks.length > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    <p className="text-xs font-medium leading-snug" style={{ color: CI.textPrimary }}>{item.titel}</p>
                    {!isExpanded && item.inhalt && (
                      <p className="text-[10px] mt-0.5 line-clamp-1" style={{ color: CI.textMuted }}>{item.inhalt}</p>
                    )}
                  </div>

                  <div className="flex-shrink-0 pt-1">
                    {isExpanded ? <ChevronUp className="w-4 h-4" style={{ color: CI.textLight }} /> : <ChevronDown className="w-4 h-4" style={{ color: CI.textLight }} />}
                  </div>
                </button>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="px-3.5 pb-3.5 space-y-3 pt-3" style={{ borderTop: `1px solid ${CI.borderLight}` }}>
                    {item.inhalt && (
                      <div className="text-xs whitespace-pre-wrap leading-relaxed rounded-lg p-3" style={{ backgroundColor: CI.bgPanel, color: CI.textSecondary, border: `1px solid ${CI.borderLight}` }}>
                        {item.inhalt}
                      </div>
                    )}

                    {(meta.abhaengigkeit || meta.anleitung || meta.quelle) && (
                      <div className="space-y-1.5">
                        {meta.abhaengigkeit && (
                          <div className="flex items-center gap-2 text-[10px] rounded-lg px-3 py-2" style={{ backgroundColor: CI.amberBg, color: '#92400E', border: `1px solid #FDE68A` }}>
                            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                            <span><strong>Abhängigkeit:</strong> {meta.abhaengigkeit}</span>
                          </div>
                        )}
                        {meta.anleitung && (
                          <div className="flex items-start gap-2 text-[10px] rounded-lg px-3 py-2" style={{ backgroundColor: CI.blueBg, color: '#1E40AF', border: `1px solid #BFDBFE` }}>
                            <ListChecks className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                            <span className="whitespace-pre-wrap"><strong>Anleitung:</strong> {meta.anleitung}</span>
                          </div>
                        )}
                        {meta.quelle && (
                          <div className="flex items-center gap-2 text-[10px]" style={{ color: CI.textMuted }}>
                            <FileText className="w-3 h-3" />
                            <span>Quelle: {meta.quelle}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {feedbacks.length > 0 && (
                      <div className="space-y-1.5">
                        <p className="text-[10px] font-medium uppercase tracking-wider" style={{ color: CI.textMuted }}>Feedback-Verlauf</p>
                        {feedbacks.map((fb: any, i: number) => (
                          <div key={i} className="text-[10px] rounded-lg p-2.5" style={{
                            backgroundColor: fb.entscheidung === 'genehmigt' ? CI.emeraldBg : fb.entscheidung === 'abgelehnt' ? CI.redLight : CI.blueBg,
                            color: fb.entscheidung === 'genehmigt' ? CI.emerald : fb.entscheidung === 'abgelehnt' ? CI.red : CI.blue,
                            border: `1px solid ${fb.entscheidung === 'genehmigt' ? '#A7F3D0' : fb.entscheidung === 'abgelehnt' ? CI.redBorder : '#BFDBFE'}`,
                          }}>
                            <div className="flex items-center gap-1.5 mb-0.5">
                              {fb.entscheidung === 'genehmigt' ? <ThumbsUp className="w-3 h-3" /> : fb.entscheidung === 'abgelehnt' ? <ThumbsDown className="w-3 h-3" /> : <Edit3 className="w-3 h-3" />}
                              <span className="font-medium">{fb.entscheidung === 'genehmigt' ? 'Genehmigt' : fb.entscheidung === 'abgelehnt' ? 'Abgelehnt' : 'Angepasst'}</span>
                              <span className="ml-auto" style={{ color: CI.textMuted }}>{formatRelativeTime(fb.datum)}</span>
                            </div>
                            {fb.text && <p className="mt-1">{fb.text}</p>}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Status ändern */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[10px] mr-1" style={{ color: CI.textMuted }}>Status:</span>
                      {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
                        const BtnIcon = cfg.icon;
                        return (
                          <button
                            key={key}
                            onClick={() => handleStatusChange(item, key)}
                            className="text-[10px] px-2 py-1 rounded-lg flex items-center gap-1 transition-colors"
                            style={{
                              backgroundColor: status === key ? cfg.bg : CI.bgSection,
                              color: status === key ? cfg.farbe : CI.textMuted,
                              fontWeight: status === key ? 600 : 400,
                            }}
                          >
                            <BtnIcon className="w-3 h-3" /> {cfg.label}
                          </button>
                        );
                      })}
                    </div>

                    {/* Feedback geben */}
                    <div className="space-y-2">
                      <textarea
                        placeholder="Feedback, Änderungswunsch oder Kommentar..."
                        value={feedbackText[item.id] || ''}
                        onChange={e => setFeedbackText(prev => ({ ...prev, [item.id]: e.target.value }))}
                        rows={2}
                        className="w-full text-xs border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-200 resize-none"
                        style={{ borderColor: CI.border, color: CI.textPrimary }}
                      />
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleFeedback(item, 'genehmigt')} className="flex items-center gap-1.5 text-[10px] font-medium text-white px-3 py-1.5 rounded-lg transition-colors" style={{ backgroundColor: CI.emerald }}>
                          <ThumbsUp className="w-3 h-3" /> Genehmigen
                        </button>
                        <button onClick={() => handleFeedback(item, 'angepasst')} className="flex items-center gap-1.5 text-[10px] font-medium px-3 py-1.5 rounded-lg transition-colors" style={{ color: CI.blue, backgroundColor: CI.blueBg }}>
                          <Edit3 className="w-3 h-3" /> Anpassen
                        </button>
                        <button onClick={() => handleFeedback(item, 'abgelehnt')} className="flex items-center gap-1.5 text-[10px] font-medium px-3 py-1.5 rounded-lg transition-colors" style={{ color: CI.red, backgroundColor: CI.redLight }}>
                          <ThumbsDown className="w-3 h-3" /> Ablehnen
                        </button>
                        <div className="flex-1" />
                        <button onClick={() => deleteEintrag(item.id)} className="p-1.5 rounded-lg transition-colors" style={{ color: CI.textLight }}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 text-[9px] pt-1" style={{ borderTop: `1px solid ${CI.borderLight}`, color: CI.textLight }}>
                      <span>Erstellt: {formatRelativeTime(item.erstellt_am)}</span>
                      <span>Von: {item.erstellt_von === 'ki_agent' ? 'Agent' : item.erstellt_von === 'system' ? 'System' : 'Du'}</span>
                      {meta.letzteAenderung && <span>Geändert: {formatRelativeTime(meta.letzteAenderung)}</span>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Sync-Banner */}
      <div className="rounded-xl p-3" style={{ background: `linear-gradient(135deg, ${CI.cyanBg}, ${CI.blueBg})`, border: `1px solid #67E8F9` }}>
        <div className="flex items-start gap-2">
          <RefreshCcw className={`w-4 h-4 flex-shrink-0 mt-0.5 ${syncing ? 'animate-spin' : ''}`} style={{ color: CI.cyan }} />
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-medium" style={{ color: '#155E75' }}>Google Drive Roadmap-Sync</p>
                <p className="text-[10px] mt-0.5" style={{ color: CI.cyan }}>
                  {ROADMAP_STATS.gesamt} Einträge • {ROADMAP_STATS.apiTodos} API-TODOs • {ROADMAP_STATS.mcpTodos} MCP-Connectoren
                </p>
              </div>
              <button
                onClick={handleSync}
                disabled={syncing}
                className="flex items-center gap-1.5 text-[10px] font-medium text-white disabled:opacity-50 px-3 py-1.5 rounded-lg transition-colors flex-shrink-0"
                style={{ backgroundColor: CI.cyan }}
              >
                {syncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                {syncing ? 'Synchronisiere...' : 'Synchronisieren'}
              </button>
            </div>
            {syncResult && (
              <div className="flex items-center gap-3 mt-2 text-[10px]">
                {syncResult.added > 0 && <span className="font-medium" style={{ color: CI.emerald }}>{syncResult.added} neu</span>}
                {syncResult.skipped > 0 && <span style={{ color: CI.textMuted }}>{syncResult.skipped} vorhanden</span>}
                {syncResult.errors > 0 && <span className="font-medium" style={{ color: CI.red }}>{syncResult.errors} Fehler</span>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Hinweis */}
      <div className="rounded-xl p-3" style={{ backgroundColor: CI.blueBg, border: `1px solid #BFDBFE` }}>
        <div className="flex items-start gap-2">
          <Brain className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: CI.blue }} />
          <div>
            <p className="text-[10px] font-medium" style={{ color: '#1E40AF' }}>Feedback-Loop aktiv</p>
            <p className="text-[10px] mt-0.5 leading-relaxed" style={{ color: CI.blue }}>
              Agenten können Features vorschlagen. Du genehmigst, lehnst ab oder passt an. Muster werden als Lernregeln gespeichert.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB: OHNE-MATCH REVIEW-QUEUE (easywerkstatt Akten ohne Gutachten-Zuordnung)
// ═══════════════════════════════════════════════════════════════════════════

interface WerkstattNoMatchRow {
  id: string;
  akten_nummer: string | null;
  kunde_name: string | null;
  kennzeichen: string | null;
  created_at: string;
  kostenvoranschlag: number | null;
  gesamtbetrag: number | null;
  quelle: string | null;
}

interface GutachtenSearchRow {
  id: string;
  akten_nummer: string | null;
  kunde_name: string | null;
  kennzeichen: string | null;
  schadendatum: string | null;
}

function GutachtenPicker({
  onSelect,
  onCancel,
}: {
  onSelect: (id: string, label: string) => void;
  onCancel: () => void;
}) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<GutachtenSearchRow[]>([]);
  const [searching, setSearching] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!search.trim()) {
      setResults([]);
      return;
    }
    timerRef.current = setTimeout(async () => {
      setSearching(true);
      // Strip PostgREST-special chars (comma, parens, dot) to prevent .or() parse errors
      const q = search.trim().replace(/[,().]/g, '');
      const { data, error } = await supabase
        .from('akten')
        .select('id, akten_nummer, kunde_name, kennzeichen, schadendatum')
        .eq('business_unit', 'gutachten')
        .eq('geloescht', false)
        .or(`akten_nummer.ilike.%${q}%,kunde_name.ilike.%${q}%,kennzeichen.ilike.%${q}%`)
        .order('created_at', { ascending: false })
        .limit(10);
      setSearching(false);
      if (error) {
        toast.error('Suche fehlgeschlagen');
        return;
      }
      setResults((data ?? []) as GutachtenSearchRow[]);
    }, 300);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [search]);

  return (
    <div
      className="mt-2 rounded-lg border p-3 space-y-2"
      style={{ backgroundColor: CI.bgPanel, borderColor: CI.border, boxShadow: CI.shadow }}
    >
      <div className="flex items-center gap-2">
        <Search className="w-3.5 h-3.5 flex-shrink-0" style={{ color: CI.textMuted }} />
        <input
          autoFocus
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Aktenzeichen, Kunde oder Kennzeichen…"
          className="flex-1 text-xs outline-none bg-transparent"
          style={{ color: CI.textPrimary }}
        />
        {searching && <Loader2 className="w-3 h-3 animate-spin flex-shrink-0" style={{ color: CI.textMuted }} />}
        <button
          onClick={onCancel}
          className="p-1 rounded hover:bg-gray-100 transition-colors"
          title="Abbrechen"
        >
          <X className="w-3 h-3" style={{ color: CI.textMuted }} />
        </button>
      </div>

      {results.length > 0 && (
        <div className="divide-y" style={{ borderColor: CI.borderLight }}>
          {results.map(r => {
            const label = [r.akten_nummer, r.kunde_name, r.kennzeichen].filter(Boolean).join(' · ');
            return (
              <button
                key={r.id}
                onClick={() => onSelect(r.id, label)}
                className="w-full text-left px-2 py-1.5 rounded text-xs transition-colors hover:bg-gray-50"
                style={{ color: CI.textPrimary }}
              >
                <span className="font-mono font-semibold">{r.akten_nummer ?? '—'}</span>
                {r.kunde_name && <span className="ml-2 opacity-70">{r.kunde_name}</span>}
                {r.kennzeichen && <span className="ml-2 opacity-50">{r.kennzeichen}</span>}
                {r.schadendatum && (
                  <span className="ml-2 opacity-40">
                    {new Date(r.schadendatum).toLocaleDateString('de-DE')}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {search.trim() && !searching && results.length === 0 && (
        <p className="text-xs text-center py-2" style={{ color: CI.textMuted }}>
          Keine Gutachten-Akten gefunden
        </p>
      )}
    </div>
  );
}

function NoMatchQueueTab() {
  const [akten, setAkten] = useState<WerkstattNoMatchRow[]>([]);
  const [stats, setStats] = useState<{ total: number; matched: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [linkingId, setLinkingId] = useState<string | null>(null);
  const [pickerForId, setPickerForId] = useState<string | null>(null);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('akten')
        .select('id, akten_nummer, kunde_name, kennzeichen, created_at, kostenvoranschlag, gesamtbetrag, quelle')
        .eq('business_unit', 'werkstatt')
        .eq('geloescht', false)
        .ilike('quelle', 'easywerkstatt%')
        .is('linked_akte_id', null)
        .order('created_at', { ascending: false })
        .limit(500);
      if (error) throw error;
      const unmatched = (data ?? []) as WerkstattNoMatchRow[];
      setAkten(unmatched);

      const { count: total } = await supabase
        .from('akten')
        .select('id', { count: 'exact', head: true })
        .eq('business_unit', 'werkstatt')
        .eq('geloescht', false)
        .ilike('quelle', 'easywerkstatt%');

      setStats({ total: total ?? 0, matched: (total ?? 0) - unmatched.length });
    } catch (err) {
      console.error('[NoMatchQueueTab]', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  const handleLink = useCallback(async (werkstattAkteId: string, gutachtenAkteId: string) => {
    setLinkingId(werkstattAkteId);
    try {
      const resp = await fetch('/api/akten-werkstatt-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'linkWerkstatt',
          gutachten_akte_id: gutachtenAkteId,
          werkstatt_akte_id: werkstattAkteId,
        }),
      });
      const result = (await resp.json()) as { error?: string };
      if (!resp.ok) throw new Error(result.error ?? 'Fehler');
      setAkten(prev => prev.filter(a => a.id !== werkstattAkteId));
      setStats(prev => prev ? { ...prev, matched: prev.matched + 1 } : null);
      setPickerForId(null);
      toast.success('Verknüpfung gesetzt');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unbekannter Fehler';
      toast.error(`Verknüpfung fehlgeschlagen: ${msg}`);
    } finally {
      setLinkingId(null);
    }
  }, []);

  const matchRate = stats && stats.total > 0 ? stats.matched / stats.total : null;

  return (
    <div className="space-y-4" data-testid="no-match-queue">
      {/* Info-Banner: Match-Quote ≥ 60% */}
      {matchRate !== null && matchRate >= 0.6 && (
        <div
          className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs"
          style={{ backgroundColor: CI.emeraldBg, border: `1px solid #A7F3D0`, color: '#065F46' }}
        >
          <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-emerald-600" />
          <span>
            <strong>{Math.round(matchRate * 100)}%</strong> der easywerkstatt-Rechnungen wurden automatisch zugeordnet
            ({stats!.matched} von {stats!.total}).
          </span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold" style={{ color: CI.textPrimary }}>
            Ohne Gutachten-Match
          </h3>
          <p className="text-xs mt-0.5" style={{ color: CI.textMuted }}>
            easywerkstatt-Rechnungen ohne verknüpfte Gutachten-Akte
          </p>
        </div>
        <button
          onClick={loadQueue}
          disabled={loading}
          className="p-1.5 rounded-lg transition-colors"
          title="Aktualisieren"
          style={{ color: CI.textMuted }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = CI.bgSection; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="w-5 h-5 animate-spin" style={{ color: CI.textLight }} />
        </div>
      ) : akten.length === 0 ? (
        <div
          className="flex flex-col items-center justify-center py-12 rounded-xl border text-center"
          style={{ borderColor: CI.borderLight, backgroundColor: CI.bgPanel }}
        >
          <CheckCircle2 className="w-8 h-8 mb-2" style={{ color: CI.emerald }} />
          <p className="text-sm font-medium" style={{ color: CI.textSecondary }}>
            Alle Rechnungen zugeordnet
          </p>
          <p className="text-xs mt-1" style={{ color: CI.textMuted }}>
            Keine easywerkstatt-Einträge ohne Gutachten-Akte.
          </p>
        </div>
      ) : (
        <div
          className="rounded-xl border overflow-hidden"
          style={{ borderColor: CI.border, boxShadow: CI.shadow }}
        >
          {/* Table header */}
          <div
            className="grid text-[10px] font-semibold px-3 py-2 uppercase tracking-wide"
            style={{
              gridTemplateColumns: '2fr 2fr 1.5fr 1.5fr 1.5fr 1fr auto',
              backgroundColor: CI.bgSection,
              borderBottom: `1px solid ${CI.borderLight}`,
              color: CI.textMuted,
            }}
          >
            <span>Rechnungs-Nr.</span>
            <span>Kunde</span>
            <span>Kennzeichen</span>
            <span>Datum</span>
            <span>Betrag</span>
            <span>Quelle</span>
            <span />
          </div>

          {/* Table rows */}
          {akten.map((akte, idx) => {
            const betrag = akte.kostenvoranschlag ?? akte.gesamtbetrag;
            const isPickerOpen = pickerForId === akte.id;
            const isLinking = linkingId === akte.id;
            return (
              <div
                key={akte.id}
                className="border-b last:border-0"
                style={{
                  backgroundColor: idx % 2 === 0 ? CI.bgZebraEven : CI.bgZebraOdd,
                  borderColor: CI.borderLight,
                }}
              >
                <div
                  className="grid items-center px-3 py-2 text-xs"
                  style={{ gridTemplateColumns: '2fr 2fr 1.5fr 1.5fr 1.5fr 1fr auto' }}
                >
                  <span className="font-mono font-semibold truncate" style={{ color: CI.textPrimary }}>
                    {akte.akten_nummer ?? '—'}
                  </span>
                  <span className="truncate" style={{ color: CI.textSecondary }}>
                    {akte.kunde_name ?? '—'}
                  </span>
                  <span className="truncate" style={{ color: CI.textSecondary }}>
                    {akte.kennzeichen ?? '—'}
                  </span>
                  <span style={{ color: CI.textMuted }}>
                    {new Date(akte.created_at).toLocaleDateString('de-DE')}
                  </span>
                  <span style={{ color: CI.textSecondary }}>
                    {betrag != null
                      ? betrag.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
                      : '—'}
                  </span>
                  <span
                    className="text-[10px] font-medium px-1.5 py-0.5 rounded-full truncate"
                    style={{ backgroundColor: CI.amberBg, color: CI.amber }}
                  >
                    {akte.quelle ?? '—'}
                  </span>
                  <button
                    onClick={() => setPickerForId(isPickerOpen ? null : akte.id)}
                    disabled={isLinking}
                    className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-md transition-colors whitespace-nowrap"
                    style={{
                      backgroundColor: isPickerOpen ? CI.redLight : CI.bgSection,
                      color: isPickerOpen ? CI.redDark : CI.textSecondary,
                      border: `1px solid ${isPickerOpen ? CI.redBorder : CI.borderLight}`,
                    }}
                  >
                    {isLinking ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Link2 className="w-3 h-3" />
                    )}
                    Verknüpfen
                  </button>
                </div>

                {isPickerOpen && (
                  <div className="px-3 pb-3">
                    <GutachtenPicker
                      onSelect={(gutachtenAkteId) => handleLink(akte.id, gutachtenAkteId)}
                      onCancel={() => setPickerForId(null)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p className="text-[10px] text-center" style={{ color: CI.textLight }}>
        {akten.length} Einträge ohne Match
        {stats && stats.total > 0 && ` · ${stats.total} gesamt importiert`}
      </p>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB: EINSTELLUNGEN
// ═══════════════════════════════════════════════════════════════════════════

function EinstellungenTab() {
  return (
    <div className="space-y-4">
      {/* Ausführungszeiten */}
      <div className="rounded-xl border overflow-hidden" style={{ borderColor: CI.border, boxShadow: CI.shadow }}>
        <div className="px-4 py-3" style={{ backgroundColor: CI.bgSection, borderBottom: `1px solid ${CI.borderLight}` }}>
          <h4 className="text-xs font-semibold flex items-center gap-2" style={{ color: CI.textPrimary }}>
            <Clock className="w-4 h-4" style={{ color: CI.textMuted }} />
            Ausführungszeiten
          </h4>
        </div>
        {[
          { name: 'Morgen-Briefing', desc: 'Tägliches Business-Briefing per E-Mail', zeit: '11:00' },
          { name: 'Nachmittag-Check', desc: 'E-Mails, Aufgaben, Fristen prüfen', zeit: '15:00' },
          { name: 'QA-Monitoring', desc: 'Websites auf Erreichbarkeit prüfen', zeit: 'Alle 6h' },
        ].map((item, i) => (
          <div
            key={i}
            className="flex items-center justify-between px-4 py-3"
            style={{
              backgroundColor: i % 2 === 0 ? CI.bgZebraEven : CI.bgZebraOdd,
              borderBottom: i < 2 ? `1px solid ${CI.borderLight}` : 'none',
            }}
          >
            <div>
              <p className="text-xs font-medium" style={{ color: CI.textSecondary }}>{item.name}</p>
              <p className="text-[10px] mt-0.5" style={{ color: CI.textMuted }}>{item.desc}</p>
            </div>
            <span className="text-xs font-mono px-2.5 py-1 rounded-lg" style={{ backgroundColor: CI.bgSection, color: CI.textSecondary }}>{item.zeit}</span>
          </div>
        ))}
      </div>

      {/* Benachrichtigungen */}
      <div className="rounded-xl border overflow-hidden" style={{ borderColor: CI.border, boxShadow: CI.shadow }}>
        <div className="px-4 py-3" style={{ backgroundColor: CI.bgSection, borderBottom: `1px solid ${CI.borderLight}` }}>
          <h4 className="text-xs font-semibold flex items-center gap-2" style={{ color: CI.textPrimary }}>
            <MessageSquare className="w-4 h-4" style={{ color: CI.textMuted }} />
            Benachrichtigungen
          </h4>
        </div>
        {[
          { label: 'E-Mail bei dringenden Vorschlägen', aktiv: true },
          { label: 'Push bei abgelaufenen Fristen', aktiv: true },
          { label: 'Tägliche Zusammenfassung', aktiv: true },
          { label: 'Jede Agenten-Aktion melden', aktiv: false },
        ].map((item, i) => (
          <div
            key={i}
            className="flex items-center justify-between px-4 py-3"
            style={{
              backgroundColor: i % 2 === 0 ? CI.bgZebraEven : CI.bgZebraOdd,
              borderBottom: i < 3 ? `1px solid ${CI.borderLight}` : 'none',
            }}
          >
            <span className="text-xs" style={{ color: CI.textSecondary }}>{item.label}</span>
            {item.aktiv ? (
              <ToggleRight className="w-5 h-5 cursor-pointer" style={{ color: CI.emerald }} />
            ) : (
              <ToggleLeft className="w-5 h-5 cursor-pointer" style={{ color: CI.textMuted }} />
            )}
          </div>
        ))}
      </div>

      {/* Sicherheit */}
      <div className="rounded-xl border overflow-hidden" style={{ borderColor: CI.border, boxShadow: CI.shadow }}>
        <div className="px-4 py-3" style={{ backgroundColor: CI.bgSection, borderBottom: `1px solid ${CI.borderLight}` }}>
          <h4 className="text-xs font-semibold flex items-center gap-2" style={{ color: CI.textPrimary }}>
            <Shield className="w-4 h-4" style={{ color: CI.textMuted }} />
            Sicherheit & Grenzen
          </h4>
        </div>
        {[
          { label: 'Max. Auto-Genehmigungen pro Tag', value: '10', type: 'text' },
          { label: 'Überweisungen immer manuell', value: true, type: 'toggle' },
          { label: 'E-Mails immer manuell', value: true, type: 'toggle' },
        ].map((item, i) => (
          <div
            key={i}
            className="flex items-center justify-between px-4 py-3"
            style={{
              backgroundColor: i % 2 === 0 ? CI.bgZebraEven : CI.bgZebraOdd,
              borderBottom: i < 2 ? `1px solid ${CI.borderLight}` : 'none',
            }}
          >
            <span className="text-xs" style={{ color: CI.textSecondary }}>{item.label}</span>
            {item.type === 'text' ? (
              <span className="text-xs font-mono px-2.5 py-1 rounded-lg" style={{ backgroundColor: CI.bgSection, color: CI.textSecondary }}>{item.value}</span>
            ) : (
              <ToggleRight className="w-5 h-5" style={{ color: CI.emerald }} />
            )}
          </div>
        ))}
      </div>

      {/* Datenquellen */}
      <div className="rounded-xl border overflow-hidden" style={{ borderColor: CI.border, boxShadow: CI.shadow }}>
        <div className="px-4 py-3" style={{ backgroundColor: CI.bgSection, borderBottom: `1px solid ${CI.borderLight}` }}>
          <h4 className="text-xs font-semibold flex items-center gap-2" style={{ color: CI.textPrimary }}>
            <Radio className="w-4 h-4" style={{ color: CI.textMuted }} />
            Verbundene Datenquellen
          </h4>
        </div>
        {[
          { name: 'Gmail', status: 'Verbunden', ok: true },
          { name: 'Google Drive', status: 'Verbunden', ok: true },
          { name: 'Supabase', status: 'Verbunden', ok: true },
          { name: 'WinValue', status: 'Login erforderlich', ok: false },
          { name: 'Superchat', status: 'API-Key vorhanden', ok: true },
          { name: 'GitHub', status: 'Verbunden', ok: true },
        ].map((source, i) => (
          <div
            key={i}
            className="flex items-center justify-between px-4 py-3"
            style={{
              backgroundColor: i % 2 === 0 ? CI.bgZebraEven : CI.bgZebraOdd,
              borderBottom: i < 5 ? `1px solid ${CI.borderLight}` : 'none',
            }}
          >
            <span className="text-xs" style={{ color: CI.textSecondary }}>{source.name}</span>
            <span className="text-[10px] font-medium px-2.5 py-1 rounded-full" style={{
              backgroundColor: source.ok ? CI.emeraldBg : CI.amberBg,
              color: source.ok ? CI.emerald : CI.amber,
            }}>
              {source.status}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// HAUPTKOMPONENTE
// ═══════════════════════════════════════════════════════════════════════════

interface AgentenKommandozentraleProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const TABS: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'chat', label: 'Chat', icon: MessageCircle },
  { id: 'vorschlaege', label: 'Vorschläge', icon: Sparkles },
  { id: 'protokoll', label: 'Protokoll', icon: History },
  { id: 'agenten', label: 'Agenten', icon: Activity },
  { id: 'regeln', label: 'Regeln', icon: Brain },
  { id: 'roadmap', label: 'Roadmap', icon: Map },
  { id: 'status', label: 'Status', icon: HeartPulse },
  { id: 'no-match-queue', label: 'Ohne Match', icon: Wrench },
  { id: 'einstellungen', label: 'Einstellungen', icon: Settings },
];

export default function AgentenKommandozentrale({ open, onOpenChange }: AgentenKommandozentraleProps) {
  const {
    vorschlaege,
    pendingVorschlaege,
    vorschlaegeProBereich,
    lernregeln,
    protokoll,
    protokollProAgent,
    agenten,
    stats,
    loading,
    saving,
    activeTab,
    setActiveTab,
    entscheiden,
    createLernregel,
    toggleLernregel,
    markAlleGelesen,
    vorschlagAktion,
    createManualVorschlag,
    prepareStellungnahme,
    mitarbeiter,
    aufgabeErstellen,
    issueInRoadmap,
    reload,
  } = useAgentenSystem();

  const heute = new Date();
  const datumStr = heute.toLocaleDateString('de-DE', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl md:max-w-2xl overflow-hidden p-0 !gap-0 [&>button:last-child]:hidden flex flex-col h-full max-h-[100dvh]"
      >
        {/* ── Sticky Header ── */}
        <div className="flex-shrink-0 sticky top-0 z-10" style={{ backgroundColor: CI.bgPanel, borderBottom: `1px solid ${CI.border}` }}>

          {/* Block 1: Titel-Zeile */}
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            <div className="min-w-0 flex-1">
              <SheetTitle className="text-base font-bold leading-tight" style={{ color: CI.textPrimary }}>
                Kommandozentrale
              </SheetTitle>
              <SheetDescription className="text-[11px] leading-tight mt-0.5" style={{ color: CI.textMuted }}>
                {datumStr}
              </SheetDescription>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0 ml-3">
              {stats.pending > 0 && (
                <button
                  onClick={markAlleGelesen}
                  className="text-[10px] font-medium px-2 py-1.5 rounded-lg transition-colors whitespace-nowrap"
                  style={{ color: CI.redDark }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = CI.redLight; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                >
                  Alle gelesen
                </button>
              )}
              <button
                onClick={reload}
                className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors"
                title="Aktualisieren"
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = CI.bgSection; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} style={{ color: CI.textMuted }} />
              </button>
              <SheetClose
                className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors"
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = CI.bgSection; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
              >
                <X className="w-4 h-4" style={{ color: CI.textMuted }} />
              </SheetClose>
            </div>
          </div>

          {/* Block 2: Stats-Chips */}
          <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 pb-2 overflow-x-auto scrollbar-hide">
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: CI.amber }} />
              <span className="text-[11px] whitespace-nowrap" style={{ color: CI.textMuted }}>
                <span className="font-semibold" style={{ color: CI.textSecondary }}>{stats.pending}</span> offen
              </span>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: CI.emerald }} />
              <span className="text-[11px] whitespace-nowrap" style={{ color: CI.textMuted }}>
                <span className="font-semibold" style={{ color: CI.textSecondary }}>{stats.approved}</span> genehmigt
              </span>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <History className="w-3 h-3" style={{ color: CI.textMuted }} />
              <span className="text-[11px] whitespace-nowrap" style={{ color: CI.textMuted }}>
                <span className="font-semibold" style={{ color: CI.textSecondary }}>{stats.protokollGesamt}</span> Aktionen
              </span>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <Brain className="w-3 h-3" style={{ color: CI.textMuted }} />
              <span className="text-[11px] whitespace-nowrap" style={{ color: CI.textMuted }}>
                <span className="font-semibold" style={{ color: CI.textSecondary }}>{stats.regeln}</span> Regeln
              </span>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <Activity className="w-3 h-3" style={{ color: CI.textMuted }} />
              <span className="text-[11px] whitespace-nowrap" style={{ color: CI.textMuted }}>
                <span className="font-semibold" style={{ color: CI.textSecondary }}>{stats.agentenAktiv || agenten.length}</span> Agenten aktiv
              </span>
            </div>
          </div>

          {/* Block 3: Tab-Navigation */}
          <div className="flex items-center overflow-x-auto scrollbar-hide" style={{ borderTop: `1px solid ${CI.borderLight}` }}>
            {TABS.map(tab => {
              const TabIcon = tab.icon;
              const isActive = activeTab === tab.id;
              const badge = tab.id === 'vorschlaege' ? stats.pending : tab.id === 'protokoll' ? stats.protokollGesamt : 0;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className="flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-3 sm:py-2.5 text-[10px] sm:text-[11px] font-medium transition-all whitespace-nowrap border-b-2 flex-shrink-0 min-h-[44px]"
                  style={{
                    color: isActive ? CI.redDark : CI.textMuted,
                    borderBottomColor: isActive ? CI.redDark : 'transparent',
                    backgroundColor: isActive ? CI.redLight : 'transparent',
                  }}
                >
                  <TabIcon className="w-3.5 h-3.5" />
                  <span>{tab.label}</span>
                  {badge > 0 && (
                    <span
                      className="min-w-[16px] h-[16px] rounded-full text-[8px] font-bold flex items-center justify-center px-1"
                      style={{
                        backgroundColor: isActive ? CI.redDark : CI.bgSection,
                        color: isActive ? '#FFFFFF' : CI.textMuted,
                      }}
                    >
                      {badge > 99 ? '99+' : badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Tab-Inhalt (scrollbar) ── */}
        <div
          className={`flex-1 overscroll-contain ${activeTab === 'chat' ? 'overflow-hidden p-3 sm:p-4' : 'overflow-y-auto px-3 sm:px-4 py-4 sm:py-5'}`}
          style={{ backgroundColor: CI.bgSection }}
        >
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin mb-3" style={{ color: CI.textLight }} />
              <p className="text-sm" style={{ color: CI.textMuted }}>System wird geladen...</p>
            </div>
          ) : (
            <>
              {activeTab === 'chat' && (
                <AgentenChatTab
                  agenten={agenten}
                  protokoll={protokoll}
                  onCreateManualVorschlag={createManualVorschlag}
                />
              )}
              {activeTab === 'vorschlaege' && (
                <VorschlaegeTab
                  vorschlaege={vorschlaege}
                  vorschlaegeProBereich={vorschlaegeProBereich}
                  onEntscheiden={entscheiden}
                  onAktion={vorschlagAktion}
                  onCreateManual={createManualVorschlag}
                  onPrepareStellungnahme={prepareStellungnahme}
                  onAufgabeErstellen={aufgabeErstellen}
                  onIssueInRoadmap={issueInRoadmap}
                  mitarbeiter={mitarbeiter}
                  saving={saving}
                  agenten={agenten}
                />
              )}
              {activeTab === 'protokoll' && (
                <ProtokollTab
                  protokoll={protokoll}
                  protokollProAgent={protokollProAgent}
                  agenten={agenten}
                />
              )}
              {activeTab === 'agenten' && (
                <AgentenTab agenten={agenten} />
              )}
              {activeTab === 'regeln' && (
                <LernregelnTab
                  regeln={lernregeln}
                  onToggle={toggleLernregel}
                  onCreate={createLernregel}
                />
              )}
              {activeTab === 'roadmap' && (
                <RoadmapTabV2 />
              )}
              {activeTab === 'status' && (
                <SystemStatusTab />
              )}
              {activeTab === 'no-match-queue' && (
                <NoMatchQueueTab />
              )}
              {activeTab === 'einstellungen' && (
                <EinstellungenTab />
              )}
            </>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="px-4 sm:px-5 py-2 sm:py-3 flex-shrink-0 safe-area-bottom" style={{ borderTop: `1px solid ${CI.borderLight}`, backgroundColor: CI.bgPanel }}>
          <p className="text-[10px] text-center" style={{ color: CI.textLight }}>
            Agenten-System V5.2 LIVE — {agenten.length} Agenten, GitHub-Roadmap-Sync, Feedback-System
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
